/**
 * Aurex One-page theme — public-site section components.
 *
 * All sections accept `{ config, items, bg, font, contrast }` props where:
 *   bg       — hex from section_configs (overrides schema suggestion)
 *   font     — font-family CSS string
 *   contrast — 'dark' or 'light' (text color scheme)
 *
 * Design notes match the Aurex spec: monochromatic palette, generous spacing,
 * subtle borders (not shadows), auto-alternating process steps, grayscale→color
 * partner/client hovers.
 */
import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, X as XIcon, Linkedin, Twitter, Globe, Calendar, ArrowRight, ArrowUpRight, Quote, Phone, Send, Loader2, Star, Clock, BookOpen, ExternalLink } from 'lucide-react';
import * as lucide from 'lucide-react';
import { toast } from 'sonner';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import MarkerClusterGroup from 'react-leaflet-cluster';
import { AUREX_FONTS, AUREX_PALETTE, aurexContrastFor } from '../lib/themeColors';
import { normalizeRichText } from '../lib/richText';
import { useT } from '../lib/i18n';
import { useLang, itemHasLocale } from '../lib/i18n';
import { getTileUrl, getTileAttribution } from '../lib/mapConfig';
import { contactAPI, blogExternalAPI, checkoutAPI } from '../lib/api';
import CaptchaWidget from './CaptchaWidget';
import { useSettings } from '../App';

import { BACKEND_URL as API } from '../lib/config';
const resolveImg = (src) => (src && src.startsWith('/api') ? `${API}${src}` : src);

// ─── Scroll-reveal wrapper (IntersectionObserver) ────────────────────────

export function Reveal({ children, delay = 0, className = '', as: Tag = 'div', once = true, ...rest }) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    // Graceful fallback for very old browsers: show immediately
    if (typeof IntersectionObserver === 'undefined') { setVisible(true); return; }
    const obs = new IntersectionObserver(entries => {
      entries.forEach(e => {
        if (e.isIntersecting) {
          setVisible(true);
          if (once) obs.unobserve(el);
        } else if (!once) {
          setVisible(false);
        }
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -40px 0px' });
    obs.observe(el);
    return () => obs.disconnect();
  }, [once]);
  return (
    <Tag ref={ref} className={`aurex-reveal ${visible ? 'aurex-in' : ''} ${className}`} style={{ transitionDelay: `${delay}ms` }} {...rest}>
      {children}
    </Tag>
  );
}


// ─── Shared helpers ──────────────────────────────────────────────────────

function fontFor(key, contrast) {
  const f = AUREX_FONTS.find(x => x.key === key);
  if (f) return f.css;
  // Spec defaults: Inter for light bg, Sora for dark bg
  return contrast === 'dark' ? "'Inter', sans-serif" : "'Sora', sans-serif";
}

function sectionStyle({ bg, font, contrast }) {
  return {
    backgroundColor: bg || '#FFFFFF',
    color: contrast === 'light' ? '#FFFFFF' : '#111827',
    fontFamily: font || fontFor(null, contrast),
  };
}

function SectionShell({ bg, font, contrast, className = '', children, ...rest }) {
  return (
    <section className={`aurex-section px-6 sm:px-10 md:px-16 lg:px-24 py-16 md:py-24 ${className}`} style={sectionStyle({ bg, font, contrast })} {...rest}>
      <div className="max-w-7xl mx-auto">{children}</div>
    </section>
  );
}

function SectionHeader({ title, subtitle, eyebrow, contrast, centered = true, sectionNumber, action }) {
  const tt = useT();
  const eyebrowText = tt(eyebrow);
  const t1 = tt(title);
  const t2 = tt(subtitle);
  if (!eyebrowText && !t1 && !t2 && !sectionNumber) return null;
  // When a section number or right-side action (CTA button) is present → left-aligned split layout.
  // Otherwise keep the default centred layout.
  const isLeft = !centered || sectionNumber != null || action != null;
  return (
    <Reveal className={`${isLeft ? 'flex items-end justify-between gap-6 flex-wrap' : 'text-center'} mb-12 md:mb-16`}>
      <div>
        {(eyebrowText || sectionNumber) && (
          <p className={`text-[11px] uppercase tracking-[0.3em] font-semibold mb-3 ${contrast === 'light' ? 'text-gray-300' : 'text-gray-500'}`}>
            {sectionNumber && <span className="mr-1" style={{ color: 'var(--color-primary)' }}>{sectionNumber}/</span>}
            {eyebrowText}
          </p>
        )}
        {t1 && <h2 className="text-3xl md:text-5xl font-bold tracking-tight">{t1}</h2>}
        {t2 && <p className={`mt-4 max-w-2xl ${!isLeft ? 'mx-auto' : ''} ${contrast === 'light' ? 'text-gray-300' : 'text-gray-600'}`}>{t2}</p>}
      </div>
      {action && <div className="shrink-0 self-end">{action}</div>}
    </Reveal>
  );
}

export function LucideIcon({ name, className = 'w-8 h-8' }) {
  if (!name) return null;
  // Normalize input (e.g. "briefcase" → "Briefcase")
  const key = name.charAt(0).toUpperCase() + name.slice(1);
  const Comp = lucide[key] || lucide[name];
  return Comp ? <Comp className={className} /> : null;
}

// ─── 1. Aurex is for you (target audience) ───────────────────────────────

export function AurexAudience({ config = {}, items = [], bg, font, contrast }) {
  const tt = useT();
  const { lang } = useLang();
  const c = { bg: bg || '#FFFFFF', font, contrast: contrast || aurexContrastFor(bg || '#FFFFFF') };
  // Locale filter: each item only renders in the locales its primary field
  // (title) has content for. Legacy plain strings show in all locales.
  const visibleItems = items.filter(i => itemHasLocale(i.title, lang));
  const cols = Math.min(visibleItems.length, 3);
  const ctaText = tt(config.cta_text);
  const ctaUrl = tt(config.cta_url);
  return (
    <SectionShell {...c} id="aurex-audience" data-testid="aurex-section-audience">
      <SectionHeader title={config.title} subtitle={config.subtitle} eyebrow={config.eyebrow} contrast={c.contrast} />
      <div className={`grid gap-8 ${cols === 1 ? 'grid-cols-1 max-w-md mx-auto' : cols === 2 ? 'grid-cols-1 md:grid-cols-2 max-w-4xl mx-auto' : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'}`}>
        {visibleItems.map((i, idx) => (
          <Reveal as="article" delay={idx * 100} key={i.id} className="border rounded-xl p-8" style={{ borderColor: c.contrast === 'light' ? 'rgba(255,255,255,.15)' : '#E5E7EB' }} data-testid={`audience-card-${i.id}`}>
            <LucideIcon name={i.icon} className="w-9 h-9 mb-5" />
            <h3 className="text-xl font-semibold mb-2">{tt(i.title)}</h3>
            {i.description && <div className={`text-sm leading-relaxed rich-text-content ${c.contrast === 'light' ? 'text-gray-300' : 'text-gray-600'}`} dangerouslySetInnerHTML={{ __html: normalizeRichText(tt(i.description)) }} />}
          </Reveal>
        ))}
      </div>
      {ctaText && ctaUrl && (
        <div className="text-center mt-12">
          <a href={ctaUrl} target={config.cta_new_tab ? '_blank' : '_self'} rel={config.cta_new_tab ? 'noopener noreferrer' : undefined} className="inline-flex items-center gap-2 px-7 py-2.5 rounded-full text-sm font-semibold transition-all hover:opacity-80" style={{ border: `1.5px solid ${c.contrast === 'light' ? 'rgba(255,255,255,0.7)' : '#111827'}`, color: c.contrast === 'light' ? '#FFFFFF' : '#111827', backgroundColor: 'transparent' }} data-testid="audience-cta">
            {ctaText} <ArrowRight className="w-4 h-4" />
          </a>
        </div>
      )}
    </SectionShell>
  );
}

// ─── 2. Our Process (vertical timeline, alternating) ─────────────────────

export function AurexProcess({ config = {}, items = [], bg, font, contrast, sectionNumber }) {
  const tt = useT();
  const { lang } = useLang();
  const c = { bg: bg || '#1F2937', font, contrast: contrast || aurexContrastFor(bg || '#1F2937') };
  const visibleItems = items.filter(i => itemHasLocale(i.title, lang));
  if (!visibleItems.length) return null;   // hide section when no items
  return (
    <SectionShell {...c} id="aurex-process" data-testid="aurex-section-process">
      <SectionHeader title={config.title} subtitle={config.subtitle} eyebrow={config.eyebrow} contrast={c.contrast} sectionNumber={sectionNumber} />
      <div className="relative max-w-3xl mx-auto">
        {/* Center vertical line */}
        <div className="absolute left-8 md:left-1/2 top-2 bottom-2 w-px md:-translate-x-px" style={{ backgroundColor: c.contrast === 'light' ? 'rgba(255,255,255,.2)' : 'rgba(17,24,39,.15)' }} />
        {visibleItems.map((step, idx) => {
          const isLeft = idx % 2 === 0;
          return (
            <Reveal delay={idx * 120} key={step.id} className={`relative flex items-start gap-6 mb-12 ${isLeft ? 'md:flex-row' : 'md:flex-row-reverse'}`} data-testid={`process-step-${step.id}`}>
              {/* Node */}
              <div className="absolute left-8 md:left-1/2 md:-translate-x-1/2 w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold" style={{ backgroundColor: c.contrast === 'light' ? '#FFFFFF' : '#111827', color: c.contrast === 'light' ? '#111827' : '#FFFFFF', boxShadow: `0 0 0 4px ${c.bg}` }}>
                {step.step_number || (idx + 1)}
              </div>
              <div className={`pl-20 md:pl-0 md:w-1/2 ${isLeft ? 'md:pr-16 md:text-right' : 'md:pl-16'}`}>
                <h3 className="text-lg md:text-xl font-semibold mb-2">{tt(step.title)}</h3>
                {step.description && <div className={`text-sm leading-relaxed rich-text-content ${c.contrast === 'light' ? 'text-gray-300' : 'text-gray-600'}`} dangerouslySetInnerHTML={{ __html: normalizeRichText(tt(step.description)) }} />}
              </div>
            </Reveal>
          );
        })}
      </div>
    </SectionShell>
  );
}

// ─── 3. Pricing ──────────────────────────────────────────────────────────

function parseFeatures(raw) {
  return String(raw || '').split(/\r?\n/).filter(Boolean).map(l => ({
    included: !l.startsWith('✗') && !l.toLowerCase().startsWith('x '),
    text: l.replace(/^✗\s*/, '').replace(/^x\s+/i, '').trim(),
  }));
}

// ─── Private Community Pro — Pricing (Litho 3-tier, featured elevated) ──────
// N plan columns; the is_featured plan is a dark navy elevated card. CMS fields:
// name, currency+price(/price_annual), period, description, badge, cta_*,
// features (✗ prefix = excluded). Switched in HomePage for privatecommunity.
export function PcpPricing({ config = {}, items = [], bg, font, favicon }) {
  const tt = useT();
  const { lang } = useLang();
  const [annual, setAnnual] = useState(false);
  const m = monoStyle(bg, font, '#F7F8F9');
  const visibleItems = items.filter(i => itemHasLocale(i.name, lang));
  if (!visibleItems.length) return null;

  const heading = m.isDark ? '#FFFFFF' : '#1a2332';
  const muted   = m.isDark ? 'rgba(255,255,255,0.6)' : '#5b6570';
  const pillBg  = m.isDark ? 'rgba(255,255,255,0.06)' : '#ffffff';
  const pillBd  = m.isDark ? 'rgba(255,255,255,0.14)' : '#ececec';
  const cardBg  = m.isDark ? 'rgba(255,255,255,0.04)' : '#ffffff';
  const cardBd  = m.isDark ? 'rgba(255,255,255,0.1)' : '#e7ece3';
  const softBtn = m.isDark ? 'rgba(255,255,255,0.08)' : 'var(--accent-tint)';
  const divider = m.isDark ? 'rgba(255,255,255,0.1)' : '#eef1ec';
  const accent  = 'var(--color-accent, #73af5f)';
  const faviconUrl = favicon ? resolveImg(favicon) : null;

  const renderHeadline = (title) => {
    const words = String(title || '').trim().split(/\s+/).filter(Boolean);
    if (words.length <= 2) return <>{title}</>;
    const first = words[0], last = words[words.length - 1], mid = words.slice(1, -1).join(' ');
    return <>{first}{' '}<span style={{ fontFamily: "'Playfair Display', Georgia, serif", fontStyle: 'italic', fontWeight: 600, color: 'var(--accent-deep)' }}>{mid}</span>{' '}{last}</>;
  };

  const eyebrow = tt(config.eyebrow);
  const title = tt(config.title);
  const subtitle = tt(config.subtitle);
  const gridCols = visibleItems.length === 1 ? 'grid-cols-1 max-w-sm mx-auto' : visibleItems.length === 2 ? 'grid-cols-1 md:grid-cols-2 max-w-3xl mx-auto' : 'grid-cols-1 md:grid-cols-3';

  return (
    <section className={monoShell} style={{ ...m.style, position: 'relative', overflow: 'hidden' }} id="aurex-pricing" data-testid="aurex-section-pricing">
      <div className="absolute inset-0 pointer-events-none" style={{ backgroundImage: 'radial-gradient(color-mix(in srgb, var(--color-accent) 14%, transparent) 1.4px, transparent 1.4px)', backgroundSize: '26px 26px', opacity: .5, WebkitMaskImage: 'radial-gradient(70% 55% at 50% 40%, #000, transparent 78%)', maskImage: 'radial-gradient(70% 55% at 50% 40%, #000, transparent 78%)' }} />
      <div className="relative max-w-7xl mx-auto">
        <div className="text-center mb-12">
          {eyebrow && (
            <div className="inline-flex items-center gap-2.5 mb-6" style={{ padding: '7px 16px 7px 8px', borderRadius: 999, background: pillBg, border: `1px solid ${pillBd}`, boxShadow: m.isDark ? 'none' : '0 2px 10px rgba(17,24,39,0.05)' }}>
              <span className="inline-flex items-center justify-center overflow-hidden" style={{ width: 30, height: 30, borderRadius: 999, background: m.isDark ? 'rgba(255,255,255,0.08)' : '#f5f7f4' }}>
                {faviconUrl ? <img src={faviconUrl} alt="" className="w-full h-full object-contain" /> : <span style={{ width: 7, height: 7, borderRadius: 999, background: accent }} />}
              </span>
              {eyebrow && <span className="text-[11px] font-semibold uppercase" style={{ letterSpacing: '.18em', color: m.isDark ? 'rgba(255,255,255,0.8)' : 'var(--accent-deep)' }}>{eyebrow}</span>}
            </div>
          )}
          {title && <h2 className="font-extrabold" style={{ fontSize: 'clamp(2rem, 3.4vw, 2.9rem)', lineHeight: 1.08, letterSpacing: '-.02em', color: heading }} data-testid="aurex-pricing-title">{renderHeadline(title)}</h2>}
          {subtitle && <p className="mx-auto mt-4" style={{ maxWidth: 520, fontSize: 16, lineHeight: 1.6, color: muted }}>{subtitle}</p>}
          {config.show_toggle && (
            <div className="flex justify-center mt-8">
              <div className="inline-flex rounded-full p-1" style={{ border: `1px solid ${m.isDark ? 'rgba(255,255,255,0.2)' : 'var(--accent-tint-2)'}`, background: m.isDark ? 'transparent' : '#fff' }}>
                {['Monthly', 'Annual'].map((lbl, i) => (
                  <button key={lbl} onClick={() => setAnnual(i === 1)} className="px-5 py-1.5 rounded-full text-xs font-semibold transition-colors" style={annual === (i === 1) ? { background: accent, color: 'var(--accent-ink)' } : { color: muted }}>{lbl}</button>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className={`grid gap-7 items-start ${gridCols}`}>
          {visibleItems.map((plan, idx) => {
            const price = annual ? (plan.price_annual || plan.price) : plan.price;
            const features = parseFeatures(tt(plan.features));
            const featured = !!plan.is_featured;
            const desc = tt(plan.description);
            const cardStyle = featured
              ? { background: '#111827', boxShadow: '0 40px 80px -34px rgba(17,24,39,.55)', transform: 'translateY(-14px)', border: '1px solid #111827' }
              : { background: cardBg, border: `1px solid ${cardBd}`, boxShadow: m.isDark ? 'none' : '0 20px 50px -34px rgba(17,24,39,.35)' };
            const nameCol = featured ? 'var(--accent-soft)' : (m.isDark ? 'var(--accent-soft)' : 'var(--accent-deep)');
            const priceCol = featured ? '#fff' : (m.isDark ? '#FFFFFF' : '#1a2332');
            const descCol = featured ? 'rgba(255,255,255,.72)' : (m.isDark ? 'rgba(255,255,255,.7)' : '#5b6570');
            const periodCol = featured ? 'rgba(255,255,255,.6)' : '#8a938a';
            const featTextCol = featured ? 'rgba(255,255,255,.9)' : (m.isDark ? 'rgba(255,255,255,.85)' : '#3b4652');
            const featMutedCol = featured ? 'rgba(255,255,255,.45)' : '#aab0a8';
            return (
              <Reveal as="article" delay={idx * 90} key={plan.id} className="rounded-[22px] p-8" style={cardStyle} data-testid={`plan-card-${plan.id}`}>
                <div className="flex items-center justify-between">
                  <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: '.16em', textTransform: 'uppercase', color: nameCol }}>{tt(plan.name)}</div>
                  {featured && plan.badge && <span style={{ background: accent, color: 'var(--accent-ink)', fontSize: 10.5, fontWeight: 700, letterSpacing: '.12em', textTransform: 'uppercase', padding: '5px 11px', borderRadius: 999 }}>{tt(plan.badge)}</span>}
                </div>
                <div className="flex items-start gap-1 mt-4">
                  <span style={{ fontWeight: 700, fontSize: 22, color: priceCol, marginTop: 8 }}>{tt(plan.currency) || '$'}</span>
                  <span style={{ fontWeight: 800, fontSize: 54, lineHeight: 1, color: priceCol }}>{tt(price)}</span>
                  {plan.period && <span className="self-end" style={{ marginBottom: 9, marginLeft: 4, fontSize: 13, color: periodCol }}>{tt(plan.period)}</span>}
                </div>
                {desc && <p style={{ marginTop: 14, fontSize: 14, lineHeight: 1.5, color: descCol }}>{desc}</p>}
                {plan.cta_text && (
                  <a href={plan.cta_url || '#'} target={plan.cta_new_tab ? '_blank' : '_self'} rel={plan.cta_new_tab ? 'noopener noreferrer' : undefined} className="block text-center mt-6 rounded-xl pcpb-plan" data-featured={featured ? '1' : '0'} style={{ padding: '13px', fontSize: 13, fontWeight: 700, letterSpacing: '.08em', background: featured ? accent : softBtn, color: featured ? 'var(--accent-ink)' : (m.isDark ? 'var(--accent-soft)' : 'var(--accent-deep)') }} data-testid={`plan-cta-${plan.id}`}>{tt(plan.cta_text)}</a>
                )}
                {features.length > 0 && (
                  <>
                    <div style={{ height: 1, background: featured ? 'rgba(255,255,255,.12)' : divider, margin: '26px 0' }} />
                    <div className="flex flex-col gap-4">
                      {features.map((f, i) => (
                        <div key={i} className="flex items-start gap-3">
                          {f.included ? (
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" style={{ flex: '0 0 auto' }}><circle cx="12" cy="12" r="11" fill={featured ? 'color-mix(in srgb, var(--color-accent) 22%, transparent)' : 'var(--accent-tint)'} /><path d="m8 12 2.5 2.5L16 9" stroke={featured ? 'var(--accent-soft)' : 'var(--accent-deep)'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
                          ) : (
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" style={{ flex: '0 0 auto' }}><circle cx="12" cy="12" r="11" fill={featured ? 'rgba(255,255,255,.08)' : '#eef0ee'} /><path d="M9 9l6 6M15 9l-6 6" stroke={featured ? 'rgba(255,255,255,.4)' : '#b6bdb4'} strokeWidth="2" strokeLinecap="round" /></svg>
                          )}
                          <span style={{ fontSize: 14.5, lineHeight: 1.4, color: f.included ? featTextCol : featMutedCol }}>{f.text}</span>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}

export function AurexPricing({ config = {}, items = [], bg, font, contrast }) {
  const tt = useT();
  const { lang } = useLang();
  const c = { bg: bg || '#F4F6F8', font, contrast: contrast || aurexContrastFor(bg || '#F4F6F8') };
  const [annual, setAnnual] = useState(false);
  const visibleItems = items.filter(i => itemHasLocale(i.name, lang));
  if (!visibleItems.length) return null;   // hide section when no items
  return (
    <SectionShell {...c} id="aurex-pricing" data-testid="aurex-section-pricing">
      <SectionHeader title={config.title} subtitle={config.subtitle} eyebrow={config.eyebrow} contrast={c.contrast} />
      {config.show_toggle && (
        <div className="flex justify-center mb-10">
          <div className="inline-flex rounded-full p-1 border" style={{ borderColor: c.contrast === 'light' ? 'rgba(255,255,255,.2)' : '#E5E7EB' }}>
            {['Monthly', 'Annual'].map((lbl, i) => (
              <button key={lbl} onClick={() => setAnnual(i === 1)} className={`px-5 py-1.5 rounded-full text-xs font-medium transition-colors ${annual === (i === 1) ? 'text-white' : ''}`} style={annual === (i === 1) ? { backgroundColor: '#111827' } : {}}>{lbl}</button>
            ))}
          </div>
        </div>
      )}
      <div className={`grid gap-6 ${visibleItems.length === 1 ? 'grid-cols-1 max-w-sm mx-auto' : visibleItems.length === 2 ? 'grid-cols-1 md:grid-cols-2 max-w-3xl mx-auto' : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'}`}>
        {visibleItems.map((plan, idx) => {
          const price = annual ? plan.price_annual || plan.price : plan.price;
          const features = parseFeatures(tt(plan.features));
          const featured = !!plan.is_featured;
          return (
            <Reveal as="article" delay={idx * 100} key={plan.id} className={`rounded-2xl p-8 flex flex-col border transition-all ${featured ? 'scale-[1.03] lg:scale-[1.05] shadow-xl' : ''}`} style={{ borderColor: featured ? '#111827' : (c.contrast === 'light' ? 'rgba(255,255,255,.15)' : '#E5E7EB'), backgroundColor: featured && c.contrast === 'dark' ? '#FFFFFF' : undefined, color: featured && c.contrast === 'dark' ? '#111827' : undefined, borderWidth: featured ? 2 : 1 }} data-testid={`plan-card-${plan.id}`}>
              {plan.badge && (
                <span className="inline-block self-start px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider mb-4" style={{ backgroundColor: '#111827', color: '#FFFFFF' }}>{tt(plan.badge)}</span>
              )}
              <h3 className="text-xl font-semibold mb-2">{tt(plan.name)}</h3>
              <div className="mb-6">
                <span className="text-4xl font-bold">{tt(plan.currency) || '$'}{tt(price)}</span>
                {plan.period && <span className="text-sm text-gray-500 ml-1">{tt(plan.period)}</span>}
              </div>
              <ul className="flex-1 space-y-3 mb-8">
                {features.map((f, i) => (
                  <li key={i} className={`flex items-start gap-2 text-sm ${!f.included ? 'line-through opacity-50' : ''}`}>
                    {f.included ? <Check className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" /> : <XIcon className="w-4 h-4 text-gray-400 shrink-0 mt-0.5" />}
                    <span>{f.text}</span>
                  </li>
                ))}
              </ul>
              {plan.cta_text && (
                <a href={plan.cta_url || '#'} target={plan.cta_new_tab ? '_blank' : '_self'} rel={plan.cta_new_tab ? 'noopener noreferrer' : undefined} className={`w-full text-center py-2.5 rounded-full text-sm font-semibold transition-colors inline-flex items-center justify-center gap-2`} style={{ backgroundColor: featured ? '#111827' : 'transparent', color: featured ? '#FFFFFF' : 'inherit', border: featured ? 'none' : '1.5px solid currentColor' }}>{tt(plan.cta_text)} {featured && <ArrowRight className="w-4 h-4" />}</a>
              )}
            </Reveal>
          );
        })}
      </div>
    </SectionShell>
  );
}

// ─── 4. Our Team ─────────────────────────────────────────────────────────

export function AurexTeam({ config = {}, items = [], bg, font, contrast }) {
  const tt = useT();
  const { lang } = useLang();
  const settings = useSettings();
  const networks = (settings.social_links || []).filter(n => n.platform);
  const c = { bg: bg || '#FFFFFF', font, contrast: contrast || aurexContrastFor(bg || '#FFFFFF') };
  const filteredItems = items.filter(i => itemHasLocale(i.name, lang));
  const limit = config.max_visible ? Number(config.max_visible) : filteredItems.length;
  const visible = filteredItems.slice(0, limit);
  const iconFor = (network) => {
    const key = String(network.icon || network.platform || '').toLowerCase().replace(/[^a-z]/g, '');
    const map = { linkedin: Linkedin, twitter: Twitter, x: Twitter, facebook: lucide.Facebook, instagram: lucide.Instagram, youtube: lucide.Youtube, github: lucide.Github, globe: Globe };
    return map[key] || Globe;
  };
  return (
    <SectionShell {...c} id="aurex-team" data-testid="aurex-section-team">
      <SectionHeader title={config.title} subtitle={config.subtitle} eyebrow={config.eyebrow} contrast={c.contrast} />
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
        {visible.map((m, idx) => {
          // `m.social_links` is a dict { [networkId]: url }. Fall back to legacy fields.
          const memberSocial = m.social_links || {};
          const entries = networks.map(n => {
            const url = memberSocial[n.id]
              || (n.platform?.toLowerCase() === 'linkedin' ? m.linkedin_url : null)
              || (n.platform?.toLowerCase() === 'twitter' || n.platform?.toLowerCase() === 'x' ? m.twitter_url : null);
            return url ? { ...n, url, Icon: iconFor(n) } : null;
          }).filter(Boolean);
          return (
            <Reveal as="article" delay={idx * 100} key={m.id} className="group" data-testid={`team-card-${m.id}`}>
              <div className="relative aspect-square rounded-xl overflow-hidden bg-gray-100">
                {m.photo_url ? <img src={m.photo_url} alt={m.name} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" /> : <div className="w-full h-full flex items-center justify-center text-4xl text-gray-300">{m.name?.[0]}</div>}
                {/* Bottom bio strip — fades in on hover, strictly inside the photo. */}
                {(m.bio || entries.length > 0) && (
                  <div className="absolute inset-x-0 bottom-0 p-4 bg-gradient-to-t from-black/85 via-black/60 to-transparent text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    {m.bio && <p className="text-xs leading-relaxed mb-2 line-clamp-3">{tt(m.bio)}</p>}
                    {entries.length > 0 && (
                      <div className="flex gap-2 flex-wrap">
                        {entries.map(s => (
                          <a key={s.id} href={s.url} target="_blank" rel="noreferrer" title={s.platform} className="p-1.5 bg-white/15 rounded hover:bg-white/35 backdrop-blur-sm transition-colors" aria-label={s.platform}>
                            <s.Icon className="w-3.5 h-3.5" />
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
              {/* Name + role: always outside the photo so they never overlap icons. */}
              <div className="pt-4 text-center">
                <h3 className="font-semibold text-base">{tt(m.name)}</h3>
                {m.role && <p className={`text-xs uppercase tracking-wider mt-1 ${c.contrast === 'light' ? 'text-gray-400' : 'text-gray-500'}`}>{tt(m.role)}</p>}
              </div>
            </Reveal>
          );
        })}
      </div>
      {config.show_view_all && config.view_all_url && tt(config.view_all_text) && (
        <div className="text-center mt-12">
          <a href={config.view_all_url} target={config.view_all_new_tab ? '_blank' : '_self'} rel={config.view_all_new_tab ? 'noopener noreferrer' : undefined} className="inline-flex items-center gap-2 px-7 py-2.5 rounded-full text-sm font-semibold transition-all hover:opacity-80" style={{ border: `1.5px solid ${c.contrast === 'light' ? 'rgba(255,255,255,0.7)' : '#111827'}`, color: c.contrast === 'light' ? '#FFFFFF' : '#111827', backgroundColor: 'transparent' }}>{tt(config.view_all_text)} <ArrowRight className="w-4 h-4" /></a>
        </div>
      )}
    </SectionShell>
  );
}

// Team — Private Community Pro variant.
// Dark "meet the team" band: eyebrow pill with the brand favicon, a mixed-weight
// headline (middle word accent italic), description on the right, and a row of
// tall portrait cards. Name + role sit below each photo; bio/social fade in on
// hover. Same {config, items} shape as AurexTeam (+ favicon).
export function PcpTeam({ config = {}, items = [], bg, font, favicon }) {
  const tt = useT();
  const { lang } = useLang();
  const settings = useSettings();
  const networks = (settings.social_links || []).filter(n => n.platform);
  // Auto-contrast: dark default (#111827) → light text; a light bg from the
  // Page Builder flips every element to dark. Never hardcode white/dark text.
  const m = monoStyle(bg, font, '#111827');
  const filtered = items.filter(i => itemHasLocale(i.name, lang));
  if (!filtered.length) return null;
  const limit = config.max_visible ? Number(config.max_visible) : filtered.length;
  const visible = filtered.slice(0, limit);

  const accent    = 'var(--color-accent)';
  const heading   = m.isDark ? '#ffffff' : '#1a2332';
  const subCol    = m.isDark ? 'rgba(255,255,255,0.72)' : '#5b6570';
  const pillBg    = m.isDark ? 'rgba(255,255,255,0.05)' : '#ffffff';
  const pillBd    = m.isDark ? 'rgba(255,255,255,0.12)' : 'color-mix(in srgb, var(--color-accent) 28%, transparent)';
  const pillIcoBg = m.isDark ? 'rgba(255,255,255,0.08)' : '#f5f7f4';
  const eyebrowCol = m.isDark ? 'rgba(255,255,255,0.8)' : 'var(--accent-strong)';
  const cardBg    = m.isDark ? 'rgba(255,255,255,0.04)' : '#eef1ec';
  const initialCol = m.isDark ? 'rgba(255,255,255,0.25)' : 'rgba(17,24,39,0.22)';
  const faviconUrl = favicon ? resolveImg(favicon) : null;
  const iconFor = (network) => {
    const key = String(network.icon || network.platform || '').toLowerCase().replace(/[^a-z]/g, '');
    const map = { linkedin: Linkedin, twitter: Twitter, x: Twitter, facebook: lucide.Facebook, instagram: lucide.Instagram, youtube: lucide.Youtube, github: lucide.Github, globe: Globe };
    return map[key] || Globe;
  };

  const renderHeadline = (title) => {
    const words = String(title || '').trim().split(/\s+/).filter(Boolean);
    if (words.length <= 2) return <>{title}</>;
    const first = words[0], last = words[words.length - 1], mid = words.slice(1, -1).join(' ');
    return (<>{first} <span style={{ fontFamily: "'Playfair Display', Georgia, serif", fontStyle: 'italic', fontWeight: 500, color: accent }}>{mid}</span> {last}</>);
  };

  return (
    <section className={monoShell} style={m.style} id="aurex-team" data-testid="aurex-section-team">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <Reveal className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-8 lg:gap-12 mb-14">
          <div className="max-w-2xl">
            {tt(config.eyebrow) && (
              <div className="inline-flex items-center gap-2.5 mb-6" style={{ padding: '7px 16px 7px 8px', borderRadius: 999, background: pillBg, border: `1px solid ${pillBd}`, boxShadow: m.isDark ? 'none' : '0 1px 3px rgba(0,0,0,0.06)' }}>
                <span className="inline-flex items-center justify-center overflow-hidden" style={{ width: 30, height: 30, borderRadius: 999, background: pillIcoBg }}>
                  {faviconUrl ? <img src={faviconUrl} alt="" style={{ width: 22, height: 'auto', display: 'block' }} /> : <span style={{ width: 12, height: 12, borderRadius: 999, background: accent }} />}
                </span>
                {tt(config.eyebrow) && <span style={{ fontSize: 12, letterSpacing: '0.18em', textTransform: 'uppercase', fontWeight: 600, color: eyebrowCol }}>{tt(config.eyebrow)}</span>}
              </div>
            )}
            {tt(config.title) && (
              <h2 data-testid="team-title" style={{ margin: 0, fontFamily: "'Sora', sans-serif", fontWeight: 800, fontSize: 'clamp(34px, 4vw, 52px)', lineHeight: 1.08, letterSpacing: '-0.02em', color: heading }}>
                {renderHeadline(tt(config.title))}
              </h2>
            )}
          </div>
          {tt(config.subtitle) && (
            <div className="lg:max-w-[560px] lg:pt-2">
              <p style={{ margin: 0, fontSize: 16, lineHeight: 1.65, color: subCol }}>{tt(config.subtitle)}</p>
            </div>
          )}
        </Reveal>

        {/* Portrait cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
          {visible.map((mem, idx) => {
            const memberSocial = mem.social_links || {};
            const entries = networks.map(n => {
              const url = memberSocial[n.id]
                || (n.platform?.toLowerCase() === 'linkedin' ? mem.linkedin_url : null)
                || (n.platform?.toLowerCase() === 'twitter' || n.platform?.toLowerCase() === 'x' ? mem.twitter_url : null);
              return url ? { ...n, url, Icon: iconFor(n) } : null;
            }).filter(Boolean);
            return (
              <Reveal as="article" delay={idx * 100} key={mem.id} className="group" data-testid={`team-card-${mem.id}`}>
                <div className="relative w-full rounded-2xl overflow-hidden" style={{ aspectRatio: '3 / 4', background: cardBg }}>
                  {mem.photo_url
                    ? <img src={mem.photo_url} alt={tt(mem.name)} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                    : <div className="w-full h-full flex items-center justify-center text-5xl" style={{ color: initialCol }}>{tt(mem.name)?.[0]}</div>}
                  {(mem.bio || entries.length > 0) && (
                    <div className="absolute inset-x-0 bottom-0 p-4 bg-gradient-to-t from-black/85 via-black/55 to-transparent text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      {mem.bio && <p className="text-sm leading-relaxed mb-2 line-clamp-3">{tt(mem.bio)}</p>}
                      {entries.length > 0 && (
                        <div className="flex gap-2 flex-wrap">
                          {entries.map(s => (
                            <a key={s.id} href={s.url} target="_blank" rel="noreferrer" title={s.platform} className="p-1.5 bg-white/15 rounded hover:bg-white/35 backdrop-blur-sm transition-colors" aria-label={s.platform}>
                              <s.Icon className="w-3.5 h-3.5" />
                            </a>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <div className="pt-4">
                  <h3 style={{ margin: 0, fontFamily: "'Sora', sans-serif", fontSize: 18, fontWeight: 700, color: heading }}>{tt(mem.name)}</h3>
                  {mem.role && <p style={{ margin: '6px 0 0', fontSize: 13.5, fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: accent }}>{tt(mem.role)}</p>}
                </div>
              </Reveal>
            );
          })}
        </div>

        {config.show_view_all && config.view_all_url && tt(config.view_all_text) && (
          <div className="mt-12">
            <PcpButton variant="link" dark={m.isDark} href={config.view_all_url} text={config.view_all_text} newTab={config.view_all_new_tab} />
          </div>
        )}
      </div>
    </section>
  );
}

// ─── 5. Events (from AUX Calendar) ───────────────────────────────────────

export function AurexEvents({ config = {}, items = [], bg, font, contrast, sectionNumber }) {
  const tt = useT();
  const { lang } = useLang();
  const c = { bg: bg || '#FFFFFF', font, contrast: contrast || aurexContrastFor(bg || '#FFFFFF') };
  const visibleItems = items.filter(e => itemHasLocale(e.title, lang));
  if (!visibleItems.length) return null;   // hide section when no events
  const btnStyle = { border: `1.5px solid ${c.contrast === 'light' ? 'rgba(255,255,255,0.7)' : '#111827'}`, color: c.contrast === 'light' ? '#FFFFFF' : '#111827', backgroundColor: 'transparent' };
  const btnCls = 'inline-flex items-center gap-2 px-7 py-2.5 rounded-full text-sm font-semibold transition-all hover:opacity-80';
  // View-all button lives in the header (right side) when items exist
  const viewAllBtn = (config.view_all_url && visibleItems.length > 0 && tt(config.view_all_text)) ? (
    <a href={config.view_all_url} target={config.view_all_new_tab ? '_blank' : '_self'} rel={config.view_all_new_tab ? 'noopener noreferrer' : undefined} className={btnCls} style={btnStyle}>
      {tt(config.view_all_text)} <ArrowRight className="w-4 h-4" />
    </a>
  ) : null;
  return (
    <SectionShell {...c} id="aurex-events" data-testid="aurex-section-events">
      <SectionHeader title={config.title} subtitle={config.subtitle} eyebrow={config.eyebrow} contrast={c.contrast} sectionNumber={sectionNumber} action={viewAllBtn} />
      {visibleItems.length === 0 ? (
        <p className="text-center text-sm" style={{ color: c.contrast === 'light' ? 'rgba(255,255,255,.6)' : '#6b7280' }}>{tt(config.empty_message) || 'No upcoming events.'}</p>
      ) : (
        <ul className="divide-y max-w-4xl mx-auto" style={{ borderColor: c.contrast === 'light' ? 'rgba(255,255,255,.1)' : '#E5E7EB' }}>
          {visibleItems.map((e, idx) => {
            const d = new Date(`${e.date}T${e.start_time || '00:00'}`);
            const day = String(d.getDate()).padStart(2, '0');
            const month = d.toLocaleDateString(undefined, { month: 'short' });
            return (
              <Reveal as="li" delay={idx * 80} key={e.id} className="py-6 flex items-center gap-6 flex-wrap md:flex-nowrap" data-testid={`event-row-${e.id}`}>
                <div className="text-center shrink-0">
                  <div className="text-4xl md:text-5xl font-bold leading-none">{day}</div>
                  <div className={`text-[10px] uppercase tracking-wider mt-1 ${c.contrast === 'light' ? 'text-gray-400' : 'text-gray-500'}`}>{month}</div>
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-base md:text-lg truncate">{tt(e.title)}</h3>
                  <p className={`text-sm truncate ${c.contrast === 'light' ? 'text-gray-400' : 'text-gray-500'}`}>
                    {e.start_time}{e.end_time ? ` – ${e.end_time}` : ''}{e.location ? ` · ${e.location}` : ''}
                  </p>
                </div>
                <Link to={`/my-account/event/${e.id}`} className="shrink-0 inline-flex items-center gap-1.5 px-5 py-2 rounded-full text-xs font-semibold transition-colors" style={btnStyle}>{tt(config.view_text) || 'View'} <ArrowRight className="w-3 h-3" /></Link>
              </Reveal>
            );
          })}
        </ul>
      )}
    </SectionShell>
  );
}

// ─── 6. Partners (dark strip, grayscale→color) ───────────────────────────

function LogoRow({ items, autoscroll, scrollSpeed, contrast, className = '' }) {
  const list = autoscroll ? [...items, ...items] : items;
  return (
    <div className={`${autoscroll ? 'overflow-hidden' : ''} ${className}`}>
      <div className={autoscroll ? 'flex gap-10 items-center' : 'flex flex-wrap gap-8 items-center justify-center'} style={autoscroll ? { animation: `aurex-scroll ${scrollSpeed || 30}s linear infinite` } : undefined}>
        {list.map((p, i) => {
          const target = p.link_target === 'internal' ? '_self' : (p.link_target || '_blank');
          const Wrap = p.link_url ? 'a' : 'div';
          return (
            <Wrap key={`${p.id}-${i}`} href={p.link_url || undefined} target={p.link_url ? target : undefined} rel={p.link_url && target === '_blank' ? 'noreferrer' : undefined} className="shrink-0 block group" data-testid={`logo-${p.id}`}>
              {p.logo_url ? (
                <img src={p.logo_url} alt={p.name} className="h-10 md:h-12 w-auto object-contain transition-all duration-300" style={{ filter: 'grayscale(100%) brightness(1.4)' }} onMouseEnter={e => (e.currentTarget.style.filter = 'grayscale(0%) brightness(1)')} onMouseLeave={e => (e.currentTarget.style.filter = 'grayscale(100%) brightness(1.4)')} />
              ) : (
                <span className={`text-sm font-medium ${contrast === 'light' ? 'text-white/50' : 'text-gray-400'} group-hover:text-inherit`}>{p.name}</span>
              )}
            </Wrap>
          );
        })}
      </div>
    </div>
  );
}

// ─── Private Community Pro — "Our Partners" (Option B: marquee strip) ───────
// Centered header (eyebrow pill w/ favicon, mixed-weight headline, subtitle) +
// full-bleed auto-scrolling marquee of partner logos/wordmarks (edge-faded).
// Reuses the shipped `aurex-scroll` keyframe. Switched in HomePage for privatecommunity.
export function PcpPartners({ config = {}, items = [], bg, font, favicon }) {
  const tt = useT();
  const { lang } = useLang();
  const m = monoStyle(bg, font, '#F7F8F9');
  const visibleItems = items.filter(i => i.visible !== false && itemHasLocale(i.name, lang));
  if (!visibleItems.length) return null;

  const accent  = 'var(--color-accent)';
  const heading = m.isDark ? '#FFFFFF' : '#1a2332';
  const muted   = m.isDark ? 'rgba(255,255,255,0.66)' : '#5b6570';
  const wm      = m.isDark ? 'rgba(255,255,255,0.5)' : '#aeb7bf';
  const pillBg  = m.isDark ? 'rgba(255,255,255,0.05)' : '#ffffff';
  const pillBd  = m.isDark ? 'rgba(255,255,255,0.12)' : 'color-mix(in srgb, var(--color-accent) 28%, transparent)';
  const faviconUrl = favicon ? resolveImg(favicon) : null;
  const speed = Number(config.scroll_speed) || 34;

  const renderHeadline = (title) => {
    const words = String(title || '').trim().split(/\s+/).filter(Boolean);
    if (words.length <= 2) return <>{title}</>;
    const first = words[0], last = words[words.length - 1], mid = words.slice(1, -1).join(' ');
    return (<>{first} <span style={{ fontFamily: "'Playfair Display', Georgia, serif", fontStyle: 'italic', fontWeight: 500, color: accent }}>{mid}</span> {last}</>);
  };

  // Repeat the set enough to overfill the viewport, so the translateX(-50%) marquee
  // loop never exposes a gap when there are only a few partners (each half must be
  // wider than the screen). Then duplicate that padded half for the seamless loop.
  const reps = Math.max(2, Math.ceil(10 / visibleItems.length));
  const half = Array.from({ length: reps }).flatMap(() => visibleItems);
  const loop = [...half, ...half];
  const itemEl = (p, i) => {
    const target = p.link_target === 'internal' ? '_self' : (p.link_target || '_blank');
    const Wrap = p.link_url ? 'a' : 'span';
    return (
      <Wrap key={`${p.id}-${i}`} href={p.link_url || undefined} target={p.link_url ? target : undefined} rel={p.link_url && target === '_blank' ? 'noreferrer' : undefined} className="group shrink-0 inline-flex items-center" style={{ color: 'inherit', textDecoration: 'none' }} data-testid={`partner-${p.id}`}>
        {p.logo_url
          ? <img src={p.logo_url} alt={tt(p.name)} className="grayscale brightness-125 group-hover:grayscale-0 group-hover:brightness-100 transition-[filter] duration-300" style={{ height: 44, width: 'auto', objectFit: 'contain' }} />
          : <span style={{ fontFamily: "'Sora', sans-serif", fontWeight: 600, fontSize: 26, letterSpacing: '0.04em', color: wm, whiteSpace: 'nowrap' }}>{tt(p.name)}</span>}
      </Wrap>
    );
  };

  return (
    <section className="aurex-section py-20 md:py-28" style={{ ...m.style, position: 'relative', overflow: 'hidden' }} id="aurex-partners" data-testid="aurex-section-partners">
      <Reveal className="px-6 sm:px-10 md:px-16 lg:px-24 max-w-3xl mx-auto text-center" style={{ marginBottom: 52 }}>
        {tt(config.eyebrow) && (
          <div className="inline-flex items-center gap-2.5 mb-5" style={{ padding: '7px 16px 7px 7px', borderRadius: 999, background: pillBg, border: `1px solid ${pillBd}`, boxShadow: m.isDark ? 'none' : '0 1px 3px rgba(0,0,0,0.06)' }}>
            <span className="inline-flex items-center justify-center overflow-hidden" style={{ width: 30, height: 30, borderRadius: 999, background: m.isDark ? 'rgba(255,255,255,0.08)' : '#f5f7f4' }}>
              {faviconUrl ? <img src={faviconUrl} alt="" style={{ width: 22, height: 'auto', display: 'block' }} /> : <span style={{ width: 12, height: 12, borderRadius: 999, background: accent }} />}
            </span>
            {tt(config.eyebrow) && <span style={{ fontSize: 12, letterSpacing: '0.18em', textTransform: 'uppercase', fontWeight: 600, color: m.isDark ? 'rgba(255,255,255,0.78)' : 'var(--accent-strong)' }}>{tt(config.eyebrow)}</span>}
          </div>
        )}
        {tt(config.title) && <h2 data-testid="partners-title" style={{ margin: '0 auto 10px', fontFamily: "'Sora', sans-serif", fontWeight: 800, fontSize: 'clamp(30px, 3.4vw, 44px)', lineHeight: 1.06, letterSpacing: '-0.02em', color: heading }}>{renderHeadline(tt(config.title))}</h2>}
        {tt(config.subtitle) && <p style={{ margin: 0, fontSize: 16, lineHeight: 1.65, color: muted }}>{tt(config.subtitle)}</p>}
      </Reveal>

      <div style={{ position: 'relative', overflow: 'hidden', WebkitMaskImage: 'linear-gradient(90deg, transparent, #000 9%, #000 91%, transparent)', maskImage: 'linear-gradient(90deg, transparent, #000 9%, #000 91%, transparent)' }}>
        <div className="flex items-center" style={{ gap: 56, width: 'max-content', animation: `aurex-scroll ${speed}s linear infinite` }}>
          {loop.map((p, i) => itemEl(p, i))}
        </div>
      </div>
    </section>
  );
}

// ─── Private Community Pro — "Our Clients" (Option C: dark divided band) ─────
// Header (eyebrow pill w/ favicon, mixed-weight headline, subtitle, outline CTA)
// + a divided matrix of client logos/wordmarks (1px gap dividers, hover tint).
// Adaptive light/dark (default dark). Switched in HomePage for privatecommunity.
export function PcpClients({ config = {}, items = [], bg, font, favicon }) {
  const tt = useT();
  const { lang } = useLang();
  const m = monoStyle(bg, font, '#0f172a');
  const visibleItems = items.filter(i => i.visible !== false && itemHasLocale(i.name, lang));
  if (!visibleItems.length) return null;

  const accent   = 'var(--color-accent)';
  const heading  = m.isDark ? '#FFFFFF' : '#1a2332';
  const muted    = m.isDark ? 'rgba(255,255,255,0.66)' : '#5b6570';
  const wm       = m.isDark ? 'rgba(255,255,255,0.55)' : '#9aa4ad';
  const divider  = m.isDark ? 'rgba(255,255,255,0.09)' : '#e7ebe4';
  const cellBg   = m.style.backgroundColor;
  const hoverBg  = m.isDark ? 'color-mix(in srgb, var(--color-accent) 6%, transparent)' : 'color-mix(in srgb, var(--color-accent) 5%, transparent)';
  const pillBg   = m.isDark ? 'rgba(255,255,255,0.05)' : '#ffffff';
  const pillBd   = m.isDark ? 'rgba(255,255,255,0.12)' : 'color-mix(in srgb, var(--color-accent) 28%, transparent)';
  const faviconUrl = favicon ? resolveImg(favicon) : null;

  const renderHeadline = (title) => {
    const words = String(title || '').trim().split(/\s+/).filter(Boolean);
    if (words.length <= 2) return <>{title}</>;
    const first = words[0], last = words[words.length - 1], mid = words.slice(1, -1).join(' ');
    return (<>{first} <span style={{ fontFamily: "'Playfair Display', Georgia, serif", fontStyle: 'italic', fontWeight: 500, color: accent }}>{mid}</span> {last}</>);
  };

  const ctaText = tt(config.cta_text);

  const cell = (p) => {
    const target = p.link_target === 'internal' ? '_self' : (p.link_target || '_blank');
    const Wrap = p.link_url ? 'a' : 'div';
    return (
      <Wrap key={p.id} href={p.link_url || undefined} target={p.link_url ? target : undefined} rel={p.link_url && target === '_blank' ? 'noreferrer' : undefined}
        className="group flex items-center justify-center" style={{ height: 130, background: cellBg, transition: 'background .25s', color: 'inherit', textDecoration: 'none' }}
        onMouseEnter={e => { e.currentTarget.style.background = hoverBg; }} onMouseLeave={e => { e.currentTarget.style.background = cellBg; }} data-testid={`client-${p.id}`}>
        {p.logo_url
          ? <img src={p.logo_url} alt={tt(p.name)} className="grayscale brightness-125 group-hover:grayscale-0 group-hover:brightness-100 transition-[filter] duration-300" style={{ height: 42, width: 'auto', objectFit: 'contain' }} />
          : <span className="group-hover:!text-current" style={{ fontFamily: "'Sora', sans-serif", fontWeight: 600, fontSize: 18, letterSpacing: '0.08em', textTransform: 'uppercase', color: wm, textAlign: 'center', padding: '0 16px' }}>{tt(p.name)}</span>}
      </Wrap>
    );
  };

  return (
    <section className={monoShell} style={{ ...m.style, position: 'relative', overflow: 'hidden' }} id="aurex-clients" data-testid="aurex-section-clients">
      <div aria-hidden="true" style={{ position: 'absolute', top: -200, right: -170, width: 560, height: 560, borderRadius: 999, background: `radial-gradient(circle, ${m.isDark ? 'color-mix(in srgb, var(--color-accent) 16%, transparent)' : 'color-mix(in srgb, var(--color-accent) 11%, transparent)'}, color-mix(in srgb, var(--color-accent) 0%, transparent) 68%)`, pointerEvents: 'none' }} />
      <div className="max-w-7xl mx-auto relative">
        <Reveal className="flex items-end justify-between mb-11 flex-wrap gap-6">
          <div style={{ maxWidth: 640 }}>
            {tt(config.eyebrow) && (
              <div className="inline-flex items-center gap-2.5 mb-5" style={{ padding: '7px 16px 7px 7px', borderRadius: 999, background: pillBg, border: `1px solid ${pillBd}`, boxShadow: m.isDark ? 'none' : '0 1px 3px rgba(0,0,0,0.06)' }}>
                <span className="inline-flex items-center justify-center overflow-hidden" style={{ width: 30, height: 30, borderRadius: 999, background: m.isDark ? 'rgba(255,255,255,0.08)' : '#f5f7f4' }}>
                  {faviconUrl ? <img src={faviconUrl} alt="" style={{ width: 22, height: 'auto', display: 'block' }} /> : <span style={{ width: 12, height: 12, borderRadius: 999, background: accent }} />}
                </span>
                {tt(config.eyebrow) && <span style={{ fontSize: 12, letterSpacing: '0.18em', textTransform: 'uppercase', fontWeight: 600, color: m.isDark ? 'rgba(255,255,255,0.78)' : 'var(--accent-strong)' }}>{tt(config.eyebrow)}</span>}
              </div>
            )}
            {tt(config.title) && <h2 data-testid="clients-title" style={{ margin: '0 0 10px', fontFamily: "'Sora', sans-serif", fontWeight: 800, fontSize: 'clamp(30px, 3.4vw, 44px)', lineHeight: 1.06, letterSpacing: '-0.02em', color: heading }}>{renderHeadline(tt(config.title))}</h2>}
            {tt(config.subtitle) && <p style={{ margin: 0, fontSize: 16, lineHeight: 1.65, color: muted }}>{tt(config.subtitle)}</p>}
          </div>
          {ctaText && (
            <PcpButton variant="link" dark={m.isDark} href={config.cta_url} text={config.cta_text} newTab={config.cta_new_tab} dataTestId="clients-view-all" />
          )}
        </Reveal>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4" style={{ gap: 1, background: divider, border: `1px solid ${divider}`, borderRadius: 16, overflow: 'hidden' }}>
          {visibleItems.map(p => cell(p))}
        </div>
      </div>
    </section>
  );
}

export function AurexPartners({ config = {}, items = [], bg, font, contrast, sectionNumber }) {
  const tt = useT();
  const { lang } = useLang();
  const visibleItems = items.filter(i => itemHasLocale(i.name, lang));
  const c = { bg: bg || '#111827', font, contrast: contrast || aurexContrastFor(bg || '#111827') };
  if (!visibleItems.length) return null;   // hide section when no items
  const btnStyle = { border: `1.5px solid ${c.contrast === 'light' ? 'rgba(255,255,255,0.7)' : '#111827'}`, color: c.contrast === 'light' ? '#FFFFFF' : '#111827', backgroundColor: 'transparent' };
  const ctaBtn = (tt(config.cta_text) && tt(config.cta_url)) ? (
    <a href={tt(config.cta_url)} target={config.cta_new_tab ? '_blank' : '_self'} rel={config.cta_new_tab ? 'noopener noreferrer' : undefined}
       className="inline-flex items-center gap-2 px-7 py-2.5 rounded-full text-sm font-semibold transition-all hover:opacity-80" style={btnStyle}>
      {tt(config.cta_text)} <ArrowRight className="w-4 h-4" />
    </a>
  ) : null;
  return (
    <SectionShell {...c} id="aurex-partners" data-testid="aurex-section-partners">
      <SectionHeader title={config.title} subtitle={config.subtitle} eyebrow={config.eyebrow} contrast={c.contrast} sectionNumber={sectionNumber} action={ctaBtn} />
      <LogoRow items={visibleItems} autoscroll={config.autoscroll} scrollSpeed={config.scroll_speed} contrast={c.contrast} />
    </SectionShell>
  );
}

// ─── 7. Our Clients (light bg, gallery style) ────────────────────────────

export function AurexClients({ config = {}, items = [], bg, font, contrast, sectionNumber }) {
  const tt = useT();
  const { lang } = useLang();
  const visibleItems = items.filter(i => itemHasLocale(i.name, lang));
  const c = { bg: bg || '#F4F6F8', font, contrast: contrast || aurexContrastFor(bg || '#F4F6F8') };
  if (!visibleItems.length) return null;   // hide section when no items
  const btnStyle = { border: `1.5px solid ${c.contrast === 'light' ? 'rgba(255,255,255,0.7)' : '#111827'}`, color: c.contrast === 'light' ? '#FFFFFF' : '#111827', backgroundColor: 'transparent' };
  const ctaBtn = (tt(config.cta_text) && tt(config.cta_url)) ? (
    <a href={tt(config.cta_url)} target={config.cta_new_tab ? '_blank' : '_self'} rel={config.cta_new_tab ? 'noopener noreferrer' : undefined}
       className="inline-flex items-center gap-2 px-7 py-2.5 rounded-full text-sm font-semibold transition-all hover:opacity-80" style={btnStyle}>
      {tt(config.cta_text)} <ArrowRight className="w-4 h-4" />
    </a>
  ) : null;
  return (
    <SectionShell {...c} id="aurex-clients" data-testid="aurex-section-clients">
      <SectionHeader title={config.title} subtitle={config.subtitle} eyebrow={config.eyebrow} contrast={c.contrast} sectionNumber={sectionNumber} action={ctaBtn} />
      <LogoRow items={visibleItems} autoscroll={config.autoscroll} scrollSpeed={config.scroll_speed || 30} contrast={c.contrast} className="py-8" />
    </SectionShell>
  );
}

// ─── 8. Video ─────────────────────────────────────────────────────────────
// Single embedded video + optional title. Supports YouTube, Vimeo and direct
// video URLs. Render picks the right <iframe> or <video> automatically.

export function buildEmbedUrl(url, autoplay) {
  if (!url) return null;
  const yt = url.match(/(?:youtube\.com\/(?:.*v=|embed\/|shorts\/)|youtu\.be\/)([\w-]{6,})/);
  if (yt) {
    const params = autoplay ? '?autoplay=1&mute=1&loop=1&playlist=' + yt[1] : '';
    return `https://www.youtube.com/embed/${yt[1]}${params}`;
  }
  const vm = url.match(/vimeo\.com\/(\d+)/);
  if (vm) return `https://player.vimeo.com/video/${vm[1]}${autoplay ? '?autoplay=1&muted=1&loop=1' : ''}`;
  return null;
}

export function AurexVideo({ config = {}, bg, font, contrast, sectionNumber }) {
  const c = { bg: bg || '#FFFFFF', font, contrast: contrast || aurexContrastFor(bg || '#FFFFFF') };
  const url = config.video_url;
  const embed = buildEmbedUrl(url, config.autoplay);
  const aspect = config.aspect_ratio || '16/9';
  const isDirect = url && !embed && /\.(mp4|webm|ogg)$/i.test(url);
  if (!url) return null;
  return (
    <SectionShell {...c} id="aurex-video" data-testid="aurex-section-video">
      {(config.eyebrow || config.title || config.subtitle || sectionNumber) && (
        <SectionHeader title={config.title} subtitle={config.subtitle} eyebrow={config.eyebrow} contrast={c.contrast} sectionNumber={sectionNumber} />
      )}
      <Reveal className="max-w-5xl mx-auto">
        <div className="relative rounded-xl overflow-hidden shadow-2xl" style={{ aspectRatio: aspect, backgroundColor: '#000' }}>
          {embed ? (
            <iframe src={embed} className="absolute inset-0 w-full h-full" frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen title={config.title || 'Video'} />
          ) : isDirect ? (
            <video src={url} poster={config.poster_url || undefined} controls={!config.autoplay} autoPlay={!!config.autoplay} muted={!!config.autoplay} loop={!!config.autoplay} playsInline className="absolute inset-0 w-full h-full object-cover" />
          ) : (
            <div className="absolute inset-0 flex items-center justify-center text-white/60 text-sm">Unsupported video URL.</div>
          )}
        </div>
      </Reveal>
    </SectionShell>
  );
}

// ─── Private Community Pro — Video section (Option A, approved 2026-08-21) ──
// Landscape video card (click-to-play) on the left + heading and an icon
// feature list on the right (CMS items: icon / title / description). Adapted
// from Litho "Browse amazing…". Switched in HomePage when theme === 'privatecommunity'.
export function PcpVideo({ config = {}, items = [], bg, font, favicon, brandName }) {
  const tt = useT();
  const { lang } = useLang();
  const [playing, setPlaying] = useState(false);
  const url = config.video_url;
  if (!url) return null;

  const aspect = config.aspect_ratio || '16/9';
  const embed = buildEmbedUrl(url, true);            // autoplay once opened
  const isDirect = url && !buildEmbedUrl(url, false) && /\.(mp4|webm|ogg)$/i.test(url);
  const effBg = bg || '#F7F8F9';
  const dark = aurexContrastFor(effBg) === 'light';
  const heading = dark ? '#ffffff' : '#1a2332';
  const muted = dark ? 'rgba(255,255,255,.72)' : '#5b6570';
  const circleBorder = dark ? 'rgba(255,255,255,.16)' : 'var(--accent-tint-2)';
  const circleBg = dark ? 'rgba(255,255,255,.04)' : '#ffffff';
  const accent = 'var(--color-accent, #73af5f)';
  const favSrc = favicon ? (favicon.startsWith('/api') ? `${API}${favicon}` : favicon) : null;

  const visibleItems = items.filter(i => itemHasLocale(i.title, lang));
  const eyebrow = tt(config.eyebrow);
  const title = tt(config.title);
  const subtitle = tt(config.subtitle);
  const word = (tt(brandName) || '').toUpperCase();

  return (
    <SectionShell bg={effBg} font={font} contrast={aurexContrastFor(effBg)} className="overflow-x-clip" id="aurex-video" data-testid="aurex-section-video">
      <div className="relative">
        {word && <div className="absolute pointer-events-none hidden lg:block" style={{ left: -10, top: -34, fontWeight: 800, fontSize: 210, letterSpacing: '-.04em', lineHeight: 1, color: 'color-mix(in srgb, var(--color-accent) 10%, transparent)', userSelect: 'none', whiteSpace: 'nowrap' }}>{word}</div>}

        <div className="relative grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* video */}
          <div className="relative">
            <div className="absolute rounded-[26px] hidden sm:block" style={{ left: 24, top: 30, right: -14, bottom: -14, background: accent, zIndex: 0 }} />
            <div className="relative rounded-[24px] overflow-hidden shadow-2xl" style={{ aspectRatio: aspect, backgroundColor: '#0f172a', zIndex: 1 }}>
              {playing && embed ? (
                <iframe src={embed} className="absolute inset-0 w-full h-full" frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen title={title || 'Video'} />
              ) : playing && isDirect ? (
                <video src={url} controls autoPlay playsInline className="absolute inset-0 w-full h-full object-cover" />
              ) : (
                <button onClick={() => setPlaying(true)} className="absolute inset-0 w-full h-full group" aria-label="Play video" data-testid="pcp-video-play">
                  {config.poster_url
                    ? <img src={config.poster_url} alt="" className="absolute inset-0 w-full h-full object-cover" />
                    : <span className="absolute inset-0" style={{ background: 'radial-gradient(70% 70% at 50% 42%, color-mix(in srgb, var(--color-accent) 16%, transparent), rgba(0,0,0,0) 62%), linear-gradient(135deg,#25303f,#131b26)' }} />}
                  <span className="absolute inset-0 flex items-center justify-center">
                    <span className="inline-flex items-center justify-center rounded-full transition-transform duration-300 group-hover:scale-110" style={{ width: 84, height: 84, backgroundColor: accent, boxShadow: '0 16px 40px -14px rgba(0,0,0,.6)' }}>
                      <svg width="30" height="30" viewBox="0 0 24 24" fill="var(--accent-ink)" style={{ marginLeft: 4 }}><path d="M8 5v14l11-7z" /></svg>
                    </span>
                  </span>
                </button>
              )}
            </div>
          </div>

          {/* heading + features */}
          <div>
            {eyebrow && (
              <div className="inline-flex items-center gap-2 rounded-full mb-5" style={{ background: dark ? 'color-mix(in srgb, var(--color-accent) 14%, transparent)' : '#fff', border: `1px solid ${dark ? 'color-mix(in srgb, var(--color-accent) 40%, transparent)' : 'var(--accent-tint-2)'}`, color: dark ? 'var(--accent-soft)' : 'var(--accent-deep)', padding: '7px 14px', fontSize: 11.5, fontWeight: 600, letterSpacing: '.18em', textTransform: 'uppercase' }} data-testid="aurex-video-eyebrow">
                {favSrc ? <img src={favSrc} alt="" style={{ width: 16, height: 16, objectFit: 'contain' }} /> : <span style={{ width: 7, height: 7, borderRadius: 999, background: accent, flex: '0 0 auto' }} />}
                <span dangerouslySetInnerHTML={{ __html: normalizeRichText(eyebrow) }} />
              </div>
            )}
            {title && <h2 className="font-bold [&_em]:italic" style={{ fontSize: 'clamp(2rem, 3.4vw, 3.25rem)', lineHeight: 1.06, letterSpacing: '-.02em', color: heading }} data-testid="aurex-video-title" dangerouslySetInnerHTML={{ __html: normalizeRichText(title) }} />}
            {subtitle && <div className="mt-4 text-base md:text-lg leading-relaxed" style={{ color: muted, maxWidth: 460 }} dangerouslySetInnerHTML={{ __html: normalizeRichText(subtitle) }} />}

            {visibleItems.length > 0 && (
              <div className="flex flex-col gap-6 mt-9">
                {visibleItems.map((i, idx) => (
                  <Reveal as="div" delay={idx * 80} key={i.id} className="flex items-start gap-5" data-testid={`aurex-video-feature-${i.id}`}>
                    <span className="flex-none inline-flex items-center justify-center rounded-full" style={{ width: 64, height: 64, border: `1.5px solid ${circleBorder}`, background: circleBg, color: 'var(--accent-deep)' }}>
                      <LucideIcon name={i.icon} className="w-6 h-6" />
                    </span>
                    <div>
                      <div className="font-semibold" style={{ fontSize: 18, color: heading }}>{tt(i.title)}</div>
                      {i.description && <div className="mt-1 rich-text-content" style={{ fontSize: 15, lineHeight: 1.55, color: muted, maxWidth: 380 }} dangerouslySetInnerHTML={{ __html: normalizeRichText(tt(i.description)) }} />}
                    </div>
                  </Reveal>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </SectionShell>
  );
}

// ─── Data hook: fetch all Aurex sections in parallel ─────────────────────

const SECTIONS_ITEMIZED = ['aurex_audience', 'aurex_process', 'aurex_pricing', 'aurex_team', 'aurex_partners', 'aurex_clients'];

// `personality` = undefined → all sections fetch global data (Aurex One-page + other themes)
// `personality` = 'business'|'lifestyle'|'personal' → PB mini-site: every section fetches
//   its personality-specific config + items, with the backend falling back to the global
//   doc when no scoped content exists for that personality.
export function useAurexSections(personality) {
  const [data, setData] = useState({});
  useEffect(() => {
    const keys = [
      ...SECTIONS_ITEMIZED, 'aurex_events', 'aurex_video', 'news_ticker',
      'aurex_services_cfg', 'aurex_testimonials_cfg', 'aurex_news_cfg', 'aurex_blog_cfg', 'aurex_locations_cfg',
      'aurex_reading_cfg', 'aurex_portfolio_cfg', 'aurex_gallery_cfg', 'aurex_model_portfolio_cfg',
    ];
    Promise.all(keys.map(k => {
      // Pass personality for every section so CMS "Content Scope" per-personality
      // data is fetched. The backend falls back to global when no scoped doc exists.
      const param = personality ? `?personality=${personality}` : '';
      return fetch(`${API}/api/public/aurex/${k}${param}`)
        .then(r => r.ok ? r.json() : { config: {}, items: [] })
        .catch(() => ({ config: {}, items: [] }));
    }))
      .then(results => {
        const map = {};
        keys.forEach((k, i) => { map[k] = results[i]; });
        setData(map);
      });
  }, [personality]);
  return data;
}

// ═════════════════════════════════════════════════════════════════════════
//   Aurex "Mono" variants of the 9 existing sections.
//   Applied on HomePage.js when active_theme === 'aurex'. All follow the
//   monochrome palette (whites/grays/darks), subtle 1px borders (no heavy
//   shadows), sans-serif typography, and scroll-reveal.
//
//   Each variant accepts `{ bg, font }` from HomePage (pulled from
//   `settings.section_configs.aurex[<section_key>]`). When supplied, they
//   override the default bg/font and the text color scheme flips
//   automatically via luminance. If blank/undefined, the hardcoded
//   "tasteful defaults" below are used.
// ═════════════════════════════════════════════════════════════════════════

// Builds monochrome section style: CMS bg overrides the fallback; text
// color derived from bg luminance so a custom-picked dark hex still reads.
// Returns { style, textClass, mutedClass, borderColor, isDark }.
function monoStyle(bg, font, fallbackBg = '#FFFFFF') {
  const effectiveBg = bg || fallbackBg;
  const isDark = aurexContrastFor(effectiveBg) === 'light';
  return {
    style: {
      backgroundColor: effectiveBg,
      color: isDark ? '#FFFFFF' : '#111827',
      fontFamily: font || "'Inter', sans-serif",
    },
    textClass: isDark ? 'text-white' : 'text-gray-900',
    mutedClass: isDark ? 'text-white/70' : 'text-gray-600',
    eyebrowClass: isDark ? 'text-white/60' : 'text-gray-500',
    borderColor: isDark ? 'rgba(255,255,255,0.15)' : '#E5E7EB',
    isDark,
  };
}

// Unified button used everywhere in the Aurex mono template. Pill (rounded-full),
// 1.5px border, matches the section's contrast automatically.
// Honors admin's "open in new tab" config per button.
function MonoButton({ href, text, newTab, m, extraClass = '', dataTestId, children }) {
  const tt = useT();
  const label = tt(text);
  if (!label && !children) return null;
  return (
    <a
      href={tt(href) || '#'}
      target={newTab ? '_blank' : '_self'}
      rel={newTab ? 'noopener noreferrer' : undefined}
      className={`inline-flex items-center gap-2 px-7 py-2.5 rounded-full text-sm font-semibold transition-all hover:opacity-80 ${extraClass}`}
      style={{ border: `1.5px solid ${m.isDark ? 'rgba(255,255,255,0.7)' : '#111827'}`, color: m.isDark ? '#FFFFFF' : '#111827', backgroundColor: 'transparent' }}
      data-testid={dataTestId}
    >
      {children || <>{label} <ArrowRight className="w-4 h-4" /></>}
    </a>
  );
}

// Private Community Pro button system — "Option A · Accent fill".
// Neutral at rest, fills with the brand green (var(--color-accent)) on hover.
// Styling lives in index.css (.pcpb*, keyed off data-contrast). Variants:
//   primary  — solid neutral (white on dark / ink on light) + arrow
//   green    — solid accent green (no arrow)
//   outline  — bordered, transparent (no arrow)
//   circle   — 52px round icon button (arrow only)
//   link     — text link with animated underline + arrow
// `dark` selects the section contrast (pass m.isDark). Honors admin newTab.
export function PcpButton({ href, text, newTab, variant = 'primary', dark = false, dataTestId, ariaLabel, className = '', children }) {
  const tt = useT();
  const label = tt(text);
  if (!label && !children && variant !== 'circle') return null;
  const contrast = dark ? 'dark' : 'light';
  const common = {
    href: (tt(href) || '#'),
    target: newTab ? '_blank' : '_self',
    rel: newTab ? 'noopener noreferrer' : undefined,
    'data-contrast': contrast,
    'data-testid': dataTestId,
  };
  if (variant === 'link') {
    return (
      <a {...common} className={`pcpb-link ${className}`}>
        {children || <>{label} <ArrowRight style={{ width: 15, height: 15 }} /></>}
      </a>
    );
  }
  const body = children || (
    variant === 'circle' ? <ArrowRight style={{ width: 18, height: 18 }} />
    : variant === 'primary' ? <>{label} <ArrowRight className="w-4 h-4" /></>
    : label /* green / outline: label only */
  );
  return (
    <a {...common} className={`pcpb pcpb--${variant} ${className}`} aria-label={variant === 'circle' && !label ? (ariaLabel || 'Next') : undefined}>
      {body}
    </a>
  );
}

const monoShell = 'aurex-section px-6 sm:px-10 md:px-16 lg:px-24 py-20 md:py-28';
const monoText = { color: '#111827', fontFamily: "'Inter', sans-serif" };

// About
export function AurexAboutMono({ data, bg, font }) {
  const tt = useT();
  const { lang } = useLang();
  // Hide the whole About section if the admin has no title translation
  // for the current locale. Legacy plain-string titles still render.
  if (!itemHasLocale(data?.title, lang)) return null;
  const img = resolveImg(data.image);
  const m = monoStyle(bg, font, '#FFFFFF');
  return (
    <section className={monoShell} style={m.style} id="about" data-testid="about-section">
      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
        <Reveal>
          {tt(data.label) && <p className={`text-[11px] uppercase tracking-[0.3em] font-semibold ${m.eyebrowClass} mb-4`}>{tt(data.label)}</p>}
          <h2 className="text-3xl md:text-5xl font-bold tracking-tight mb-6" data-testid="about-title">{tt(data.title)}</h2>
          <div className="w-12 h-px mb-6" style={{ backgroundColor: m.isDark ? 'rgba(255,255,255,0.8)' : '#111827' }} />
          {tt(data.description) && <div className={`${m.mutedClass} leading-relaxed rich-text-content`} dangerouslySetInnerHTML={{ __html: normalizeRichText(tt(data.description)) }} />}
          <div className="flex items-center gap-6 mt-8 flex-wrap">
            {data.phone && <div className="flex items-center gap-3"><div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ borderWidth: 1, borderStyle: 'solid', borderColor: m.borderColor }}><Phone className="w-4 h-4" /></div><div><p className={`text-[11px] uppercase tracking-wider ${m.eyebrowClass}`}>Call us</p><p className="text-sm font-semibold">{data.phone}</p></div></div>}
            {tt(data.signature_name) && <div className="pl-6" style={{ borderLeft: `1px solid ${m.borderColor}` }}><p className="font-semibold">{tt(data.signature_name)}</p>{tt(data.signature_title) && <p className={`text-xs ${m.mutedClass}`}>{tt(data.signature_title)}</p>}</div>}
          </div>
          {tt(data.button_text) && <div className="mt-8"><MonoButton href={data.button_url} text={data.button_text} newTab={data.button_open_in_new_tab} m={m} dataTestId="about-cta-btn" /></div>}
        </Reveal>
        {img && <Reveal delay={120}><img src={img} alt="" className="w-full rounded-xl object-cover aspect-[4/3]" style={{ borderWidth: 1, borderStyle: 'solid', borderColor: m.borderColor }} /></Reveal>}
      </div>
    </section>
  );
}

// About — Private Community Pro variant (Option B).
// Founder photo on the left over a green offset panel with a dark "Call us"
// card overlapping the corner; eyebrow pill + mixed-weight headline + rich
// description + button/signature on the right, on a soft band. Same
// {data} shape as AurexAboutMono (+ favicon).
export function PcpAbout({ data, bg, font, favicon }) {
  const tt = useT();
  const { lang } = useLang();
  if (!itemHasLocale(data?.title, lang)) return null;
  const m = monoStyle(bg, font, '#f7f8f9');
  const img = resolveImg(data.image);
  const accent = 'var(--color-accent)';
  const faviconUrl = favicon ? resolveImg(favicon) : null;
  const heading = m.isDark ? '#ffffff' : '#111827';
  const body    = m.isDark ? 'rgba(255,255,255,0.72)' : '#4b5563';
  const muted   = m.isDark ? 'rgba(255,255,255,0.55)' : '#9ca3af';
  const pillBg  = m.isDark ? 'rgba(255,255,255,0.06)' : '#ffffff';
  const pillBd  = m.isDark ? 'rgba(255,255,255,0.14)' : '#e7e9ea';

  const renderHeadline = (title) => {
    const words = String(title || '').trim().split(/\s+/).filter(Boolean);
    if (words.length <= 2) return <>{title}</>;
    const first = words[0], last = words[words.length - 1], mid = words.slice(1, -1).join(' ');
    return (<>{first} <span style={{ fontFamily: "'Playfair Display', Georgia, serif", fontStyle: 'italic', fontWeight: 500, color: accent }}>{mid}</span> {last}</>);
  };

  return (
    <section className={monoShell} style={m.style} id="about" data-testid="about-section">
      <div className="max-w-7xl mx-auto overflow-hidden">
        <div className="grid grid-cols-1 lg:grid-cols-[0.92fr_1.08fr] gap-12 lg:gap-20 items-center">
          {/* Left: image + offset panel + overlap card */}
          <Reveal className="relative" style={{ padding: '0 18px 40px 0' }}>
            <div style={{ position: 'absolute', right: 0, top: 26, bottom: 0, left: 44, borderRadius: 20, background: accent, opacity: 0.16 }} />
            <div className="relative w-full overflow-hidden" style={{ borderRadius: 20, aspectRatio: '4 / 5', boxShadow: '0 20px 50px rgba(17,24,39,0.12)', background: m.isDark ? 'rgba(255,255,255,0.05)' : '#e9edf1' }}>
              {img
                ? <img src={img} alt={tt(data.signature_name) || 'Founder'} className="w-full h-full object-cover" />
                : <div className="w-full h-full flex items-center justify-center text-5xl" style={{ color: muted }}>{(tt(data.signature_name) || 'A')[0]}</div>}
            </div>
            {data.phone && (
              <div className="flex items-center gap-4" style={{ position: 'absolute', right: -6, bottom: 0, background: '#111827', color: '#fff', borderRadius: 16, padding: '20px 24px', boxShadow: '0 18px 44px rgba(17,24,39,0.28)' }}>
                <span className="inline-flex items-center justify-center" style={{ width: 44, height: 44, borderRadius: 12, background: accent }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--accent-ink)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.9.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z"/></svg>
                </span>
                <div>
                  <div style={{ fontSize: 11, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.55)', fontWeight: 600 }}>Call us</div>
                  <div style={{ fontFamily: "'Sora', sans-serif", fontWeight: 700, fontSize: 17 }}>{data.phone}</div>
                </div>
              </div>
            )}
          </Reveal>

          {/* Right: text */}
          <Reveal delay={120}>
            {(faviconUrl || tt(data.label)) && (
              <div className="inline-flex items-center gap-2.5 mb-6" style={{ padding: '7px 16px 7px 8px', borderRadius: 999, background: pillBg, border: `1px solid ${pillBd}`, boxShadow: m.isDark ? 'none' : '0 2px 10px rgba(17,24,39,0.05)' }}>
                <span className="inline-flex items-center justify-center overflow-hidden" style={{ width: 30, height: 30, borderRadius: 999, background: m.isDark ? 'rgba(255,255,255,0.08)' : '#f0f3f0' }}>
                  {faviconUrl ? <img src={faviconUrl} alt="" style={{ width: 22, height: 'auto', display: 'block' }} /> : <span style={{ width: 12, height: 12, borderRadius: 999, background: accent }} />}
                </span>
                {tt(data.label) && <span style={{ fontSize: 12, letterSpacing: '0.18em', textTransform: 'uppercase', fontWeight: 600, color: m.isDark ? 'rgba(255,255,255,0.75)' : '#374151' }}>{tt(data.label)}</span>}
              </div>
            )}
            {tt(data.title) && (
              <h2 data-testid="about-title" style={{ margin: '0 0 24px', fontFamily: "'Sora', sans-serif", fontWeight: 800, fontSize: 'clamp(32px, 3.6vw, 48px)', lineHeight: 1.1, letterSpacing: '-0.02em', color: heading }}>
                {renderHeadline(tt(data.title))}
              </h2>
            )}
            {tt(data.description) && <div className="rich-text-content" style={{ color: body, lineHeight: 1.7, fontSize: 16 }} dangerouslySetInnerHTML={{ __html: normalizeRichText(tt(data.description)) }} />}
            <div className="flex items-center gap-5 flex-wrap mt-8">
              {tt(data.button_text) && (
                <PcpButton variant="primary" dark={m.isDark} href={data.button_url} text={data.button_text} newTab={data.button_open_in_new_tab} dataTestId="about-cta-btn" />
              )}
              {tt(data.signature_name) && (
                <div className="flex items-center gap-2.5">
                  <span style={{ fontFamily: "'Sora', sans-serif", fontWeight: 700, fontSize: 15, color: heading }}>{tt(data.signature_name)}</span>
                  {tt(data.signature_title) && <><span style={{ width: 4, height: 4, borderRadius: 999, background: '#cbd5e1' }} /><span style={{ fontSize: 12, letterSpacing: '0.06em', textTransform: 'uppercase', color: muted }}>{tt(data.signature_title)}</span></>}
                </div>
              )}
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

// Services
export function AurexServicesMono({ services, bg, font, cmsConfig = {} }) {
  const tt = useT();
  const { lang } = useLang();
  const filtered = (services || []).filter(s => itemHasLocale(s.title, lang));
  if (!filtered.length) return null;
  const m = monoStyle(bg, font, '#F9FAFB');
  const clean = (html) => (html || '').replace(/&nbsp;/g, ' ').replace(/\u00A0/g, ' ').replace(/<[^>]*>/g, '');
  // Each service card is only linked when the admin has explicitly set an
  // external URL. Otherwise the card renders as a passive display — the
  // website stays informational without forcing an internal service page.
  const hasLink = (s) => !!(s.external_url && String(s.external_url).trim());
  return (
    <section className={monoShell} style={m.style} id="services" data-testid="services-section">
      <div className="max-w-7xl mx-auto">
        <Reveal className="text-center mb-14">
          {tt(cmsConfig.eyebrow) && <p className={`text-[11px] uppercase tracking-[0.3em] font-semibold ${m.eyebrowClass} mb-3`}>{tt(cmsConfig.eyebrow)}</p>}
          {tt(cmsConfig.title) && <h2 className="text-3xl md:text-5xl font-bold tracking-tight" data-testid="services-title">{tt(cmsConfig.title)}</h2>}
          {tt(cmsConfig.subtitle) && <p className={`mt-4 max-w-2xl mx-auto ${m.mutedClass}`}>{tt(cmsConfig.subtitle)}</p>}
        </Reveal>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-10 md:gap-12">
          {filtered.slice(0, 6).map((s, idx) => {
            const linked = hasLink(s);
            const target = linked && s.open_in_new_tab ? '_blank' : '_self';
            const commonProps = {
              delay: idx * 80,
              key: s.id,
              className: `flex flex-col items-center text-center group ${linked ? 'cursor-pointer' : ''}`,
              'data-testid': `service-card-${s.id}`,
            };
            const linkProps = linked ? { as: 'a', href: s.external_url, target, rel: target === '_blank' ? 'noopener noreferrer' : undefined } : {};
            return (
              <Reveal {...commonProps} {...linkProps}>
                {s.image ? (
                  <div className="w-full aspect-[4/3] rounded-lg overflow-hidden mb-6 relative">
                    <img src={resolveImg(s.image)} alt={tt(s.title)} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" style={{ filter: m.isDark ? 'grayscale(15%)' : 'grayscale(25%)' }} />
                  </div>
                ) : (
                  <div className="w-full aspect-[4/3] rounded-lg mb-6 flex items-center justify-center" style={{ backgroundColor: m.isDark ? 'rgba(255,255,255,0.05)' : '#F3F4F6' }}>
                    <lucide.Briefcase className="w-10 h-10 opacity-30" />
                  </div>
                )}
                <h3 className={`font-bold text-xl md:text-2xl mb-3 ${linked ? 'group-hover:underline underline-offset-4' : ''}`} style={{ fontFamily: "'Playfair Display', serif" }}>{tt(s.title)}</h3>
                {(s.short_description || s.description) && (
                  <p className={`text-sm leading-relaxed ${m.mutedClass} max-w-xs`}>{clean(tt(s.short_description) || tt(s.description)).slice(0, 130)}</p>
                )}
              </Reveal>
            );
          })}
        </div>
        {tt(cmsConfig.cta_text) && (
          <Reveal delay={200} className="text-center mt-14">
            <MonoButton href={cmsConfig.cta_url} text={cmsConfig.cta_text} newTab={cmsConfig.cta_new_tab} m={m} dataTestId="services-view-all" />
          </Reveal>
        )}
      </div>
    </section>
  );
}

// Services — Private Community Pro variant.
// "Portfolio highlights" layout: eyebrow pill with the brand favicon, a
// mixed-weight headline, description + CTA on the right, and a row of staggered
// image cards each with a title, short subtitle and a circular arrow button.
// Same signature as AurexServicesMono (+ favicon) so it drops into HomePage.
export function PcpServices({ services, bg, font, cmsConfig = {}, favicon }) {
  const tt = useT();
  const { lang } = useLang();
  const filtered = (services || []).filter(s => itemHasLocale(s.title, lang));
  if (!filtered.length) return null;
  const m = monoStyle(bg, font, '#FFFFFF');
  const strip = (html) => (html || '').replace(/&nbsp;/g, ' ').replace(/ /g, ' ').replace(/<[^>]*>/g, '').trim();

  const heading = m.isDark ? '#FFFFFF' : '#111827';
  const muted   = m.isDark ? 'rgba(255,255,255,0.55)' : '#9ca3af';
  const desc    = m.isDark ? 'rgba(255,255,255,0.72)' : '#6b7280';
  const pillBg  = m.isDark ? 'rgba(255,255,255,0.06)' : '#ffffff';
  const pillBd  = m.isDark ? 'rgba(255,255,255,0.14)' : '#ececec';
  const accent  = 'var(--color-accent)';
  const faviconUrl = favicon ? resolveImg(favicon) : null;
  const heights = [420, 348, 430, 388]; // staggered, cycles every 4 cards
  const cardTints = [
    'linear-gradient(150deg,#2b3440,#4b5563)',
    'linear-gradient(150deg,#3a3f46,#6b7280)',
    'linear-gradient(150deg,#1f2937,#3f4956)',
    'linear-gradient(150deg,#2f3742,#565f6b)',
  ];

  // Mixed-weight headline: first + last word upright/bold, the middle italic
  // (Playfair) and muted — mirrors the reference regardless of the CMS title.
  const renderHeadline = (title) => {
    const words = String(title || '').trim().split(/\s+/).filter(Boolean);
    if (words.length <= 2) return <>{title}</>;
    const first = words[0];
    const last = words[words.length - 1];
    const mid = words.slice(1, -1).join(' ');
    return (
      <>
        {first}{' '}
        <span style={{ fontFamily: "'Playfair Display', Georgia, serif", fontStyle: 'italic', fontWeight: 500, color: muted }}>{mid}</span>{' '}
        {last}
      </>
    );
  };

  return (
    <section className={monoShell} style={m.style} id="services" data-testid="services-section">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <Reveal className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-8 lg:gap-12 mb-14">
          <div className="max-w-2xl">
            {tt(cmsConfig.eyebrow) && (
              <div className="inline-flex items-center gap-2.5 mb-6" style={{ padding: '7px 16px 7px 8px', borderRadius: 999, background: pillBg, border: `1px solid ${pillBd}`, boxShadow: m.isDark ? 'none' : '0 2px 10px rgba(17,24,39,0.05)' }}>
                <span className="inline-flex items-center justify-center overflow-hidden" style={{ width: 30, height: 30, borderRadius: 999, background: m.isDark ? 'rgba(255,255,255,0.08)' : '#f5f7f4' }}>
                  {faviconUrl
                    ? <img src={faviconUrl} alt="" style={{ width: 22, height: 'auto', display: 'block' }} />
                    : <span style={{ width: 12, height: 12, borderRadius: 999, background: accent }} />}
                </span>
                {tt(cmsConfig.eyebrow) && <span style={{ fontSize: 12, letterSpacing: '0.18em', textTransform: 'uppercase', fontWeight: 600, color: m.isDark ? 'rgba(255,255,255,0.75)' : '#374151' }}>{tt(cmsConfig.eyebrow)}</span>}
              </div>
            )}
            {tt(cmsConfig.title) && (
              <h2 data-testid="services-title" style={{ margin: 0, fontFamily: "'Sora', sans-serif", fontWeight: 800, fontSize: 'clamp(34px, 4vw, 52px)', lineHeight: 1.08, letterSpacing: '-0.02em', color: heading }}>
                {renderHeadline(tt(cmsConfig.title))}
              </h2>
            )}
          </div>
          <div className="lg:max-w-[560px] lg:pt-2">
            {tt(cmsConfig.subtitle) && <p style={{ margin: '0 0 24px', fontSize: 16, lineHeight: 1.65, color: desc }}>{tt(cmsConfig.subtitle)}</p>}
            {tt(cmsConfig.cta_text) && (
              <PcpButton variant="outline" dark={m.isDark} href={cmsConfig.cta_url} text={cmsConfig.cta_text} newTab={cmsConfig.cta_new_tab} dataTestId="services-view-all" />
            )}
          </div>
        </Reveal>

        {/* Staggered image cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 items-start">
          {filtered.slice(0, 8).map((s, idx) => {
            const linked = !!(s.external_url && String(s.external_url).trim());
            const target = linked && s.open_in_new_tab ? '_blank' : '_self';
            const img = s.image ? resolveImg(s.image) : null;
            const sub = strip(tt(s.short_description) || tt(s.description)).slice(0, 46);
            const Arrow = (
              <span className="flex items-center justify-center flex-shrink-0" style={{ width: 42, height: 42, borderRadius: 999, background: accent }}>
                <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="var(--accent-ink)" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M7 17 17 7M9 7h8v8" /></svg>
              </span>
            );
            return (
              <Reveal key={s.id} delay={idx * 80} className="flex flex-col group">
                <div className="w-full rounded-2xl overflow-hidden relative" style={{ height: heights[idx % 4] }}>
                  {img
                    ? <img src={img} alt={tt(s.title)} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                    : <div className="w-full h-full" style={{ background: cardTints[idx % 4] }} />}
                </div>
                <div className="flex items-start justify-between gap-3 mt-5">
                  <div>
                    <h3 style={{ margin: 0, fontFamily: "'Sora', sans-serif", fontSize: 20, fontWeight: 700, color: heading }}>{tt(s.title)}</h3>
                    {sub && <p style={{ margin: '6px 0 0', fontSize: 14, color: muted }}>{sub}</p>}
                  </div>
                  {linked
                    ? <a href={s.external_url} target={target} rel={target === '_blank' ? 'noopener noreferrer' : undefined} aria-label={tt(s.title)}>{Arrow}</a>
                    : Arrow}
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// ─── Private Community Pro — "Aurex is for you" (Audience) ─────────────────
// Option A "Editorial + block stack": left pitch column (eyebrow pill w/ favicon,
// mixed-weight headline, subtitle, CTA) + right dynamic stack of block cards.
// Each item = icon + title + rich-text description; the stack grows with N items.
// Switched in HomePage for privatecommunity. Falls back to AurexAudience otherwise.
export function PcpAudience({ config = {}, items = [], bg, font, favicon }) {
  const tt = useT();
  const { lang } = useLang();
  const m = monoStyle(bg, font, '#FFFFFF');
  const visibleItems = items.filter(i => itemHasLocale(i.title, lang));

  const accent    = 'var(--color-accent)';
  const heading   = m.isDark ? '#FFFFFF' : '#1a2332';
  const muted     = m.isDark ? 'rgba(255,255,255,0.72)' : '#5b6570';
  const descColor = m.isDark ? 'rgba(255,255,255,0.68)' : '#4a5560';
  const cardBg    = m.isDark ? 'rgba(255,255,255,0.035)' : '#fbfcfa';
  const cardBd    = m.isDark ? 'rgba(255,255,255,0.08)' : '#e7ebe4';
  const chipBg    = m.isDark ? 'color-mix(in srgb, var(--color-accent) 16%, transparent)' : 'color-mix(in srgb, var(--color-accent) 12%, transparent)';
  const chipIco   = m.isDark ? 'var(--color-accent)' : 'var(--accent-strong)';
  const pillBg    = m.isDark ? 'rgba(255,255,255,0.05)' : '#ffffff';
  const pillBd    = m.isDark ? 'rgba(255,255,255,0.12)' : 'color-mix(in srgb, var(--color-accent) 28%, transparent)';
  const faviconUrl = favicon ? resolveImg(favicon) : null;

  const ctaText = tt(config.cta_text);

  // Mixed-weight headline: first + last word upright, the middle in Playfair
  // italic + accent green (matches PcpTeam/PcpAbout). Needs 3+ words.
  const renderHeadline = (title) => {
    const words = String(title || '').trim().split(/\s+/).filter(Boolean);
    if (words.length <= 2) return <>{title}</>;
    const first = words[0], last = words[words.length - 1], mid = words.slice(1, -1).join(' ');
    return (<>{first} <span style={{ fontFamily: "'Playfair Display', Georgia, serif", fontStyle: 'italic', fontWeight: 500, color: accent }}>{mid}</span> {last}</>);
  };

  return (
    <section className={monoShell} style={{ ...m.style, position: 'relative', overflow: 'hidden' }} id="aurex-audience" data-testid="aurex-section-audience">
      {!m.isDark && <div aria-hidden="true" style={{ position: 'absolute', top: -180, right: -160, width: 520, height: 520, borderRadius: 999, background: 'radial-gradient(circle, color-mix(in srgb, var(--color-accent) 13%, transparent), color-mix(in srgb, var(--color-accent) 0%, transparent) 70%)', pointerEvents: 'none' }} />}

      <div className="max-w-7xl mx-auto relative">
        <div className="grid grid-cols-1 lg:grid-cols-[400px,minmax(0,1fr)] gap-12 lg:gap-16 items-start">

          {/* LEFT — pitch */}
          <Reveal>
            {tt(config.eyebrow) && (
              <div className="inline-flex items-center gap-2.5 mb-6" style={{ padding: '7px 16px 7px 7px', borderRadius: 999, background: pillBg, border: `1px solid ${pillBd}`, boxShadow: m.isDark ? 'none' : '0 1px 3px rgba(0,0,0,0.06)' }}>
                <span className="inline-flex items-center justify-center overflow-hidden" style={{ width: 30, height: 30, borderRadius: 999, background: m.isDark ? 'rgba(255,255,255,0.08)' : '#f5f7f4' }}>
                  {faviconUrl ? <img src={faviconUrl} alt="" style={{ width: 22, height: 'auto', display: 'block' }} /> : <span style={{ width: 12, height: 12, borderRadius: 999, background: accent }} />}
                </span>
                {tt(config.eyebrow) && <span style={{ fontSize: 12, letterSpacing: '0.18em', textTransform: 'uppercase', fontWeight: 600, color: m.isDark ? 'rgba(255,255,255,0.78)' : 'var(--accent-strong)' }}>{tt(config.eyebrow)}</span>}
              </div>
            )}
            {tt(config.title) && (
              <h2 data-testid="audience-title" style={{ margin: 0, fontFamily: "'Sora', sans-serif", fontWeight: 800, fontSize: 'clamp(34px, 4vw, 50px)', lineHeight: 1.06, letterSpacing: '-0.02em', color: heading }}>
                {renderHeadline(tt(config.title))}
              </h2>
            )}
            {tt(config.subtitle) && <p style={{ margin: '22px 0 0', fontSize: 17, lineHeight: 1.7, color: muted, maxWidth: 360 }}>{tt(config.subtitle)}</p>}
            {ctaText && (
              <div className="mt-8">
                <PcpButton variant="primary" dark={m.isDark} href={config.cta_url} text={config.cta_text} newTab={config.cta_new_tab} dataTestId="audience-cta" />
              </div>
            )}
          </Reveal>

          {/* RIGHT — dynamic block stack */}
          <div className="flex flex-col gap-4">
            {visibleItems.map((i, idx) => (
              <Reveal as="article" delay={idx * 90} key={i.id} className="flex items-start gap-5" style={{ border: `1px solid ${cardBd}`, borderRadius: 20, padding: '28px 32px', background: cardBg }} data-testid={`audience-card-${i.id}`}>
                <span className="inline-flex items-center justify-center flex-none" style={{ width: 46, height: 46, borderRadius: 13, background: chipBg, color: chipIco }}>
                  <LucideIcon name={i.icon} className="w-[22px] h-[22px]" />
                </span>
                <div className="min-w-0">
                  <h3 style={{ margin: 0, fontFamily: "'Sora', sans-serif", fontSize: 19, fontWeight: 700, color: heading }}>{tt(i.title)}</h3>
                  {tt(i.description) && <div className="rich-text-content mt-3" style={{ fontSize: 15, lineHeight: 1.55, color: descColor }} dangerouslySetInnerHTML={{ __html: normalizeRichText(tt(i.description)) }} />}
                </div>
              </Reveal>
            ))}
          </div>

        </div>
      </div>
    </section>
  );
}

// ─── Private Community Pro — "Our process" (Process) ───────────────────────
// Option B "Dark vertical rail": left pitch column (eyebrow pill w/ favicon,
// mixed-weight headline, subtitle) + right vertical numbered rail. Each step =
// numbered node (step_number) + title + rich-text description; the last node
// inverts as the "outcome". Adaptive light/dark; scales with N steps.
// Switched in HomePage for privatecommunity. Falls back to AurexProcess otherwise.
export function PcpProcess({ config = {}, items = [], bg, font, favicon }) {
  const tt = useT();
  const { lang } = useLang();
  const m = monoStyle(bg, font, '#0f172a');
  const visibleItems = items.filter(i => itemHasLocale(i.title, lang));
  if (!visibleItems.length) return null;

  const accent    = 'var(--color-accent)';
  const heading   = m.isDark ? '#FFFFFF' : '#1a2332';
  const muted     = m.isDark ? 'rgba(255,255,255,0.66)' : '#5b6570';
  const descColor = m.isDark ? 'rgba(255,255,255,0.70)' : '#4a5560';
  const pillBg    = m.isDark ? 'rgba(255,255,255,0.05)' : '#ffffff';
  const pillBd    = m.isDark ? 'rgba(255,255,255,0.12)' : 'color-mix(in srgb, var(--color-accent) 28%, transparent)';
  const railGrad  = m.isDark ? 'linear-gradient(180deg, color-mix(in srgb, var(--color-accent) 70%, transparent), color-mix(in srgb, var(--color-accent) 15%, transparent))' : 'linear-gradient(180deg, color-mix(in srgb, var(--color-accent) 60%, transparent), color-mix(in srgb, var(--color-accent) 12%, transparent))';
  const faviconUrl = favicon ? resolveImg(favicon) : null;

  const renderHeadline = (title) => {
    const words = String(title || '').trim().split(/\s+/).filter(Boolean);
    if (words.length <= 2) return <>{title}</>;
    const first = words[0], last = words[words.length - 1], mid = words.slice(1, -1).join(' ');
    return (<>{first} <span style={{ fontFamily: "'Playfair Display', Georgia, serif", fontStyle: 'italic', fontWeight: 500, color: accent }}>{mid}</span> {last}</>);
  };

  return (
    <section className={monoShell} style={{ ...m.style, position: 'relative', overflow: 'hidden' }} id="aurex-process" data-testid="aurex-section-process">
      <div aria-hidden="true" style={{ position: 'absolute', top: -200, right: -170, width: 560, height: 560, borderRadius: 999, background: `radial-gradient(circle, ${m.isDark ? 'color-mix(in srgb, var(--color-accent) 18%, transparent)' : 'color-mix(in srgb, var(--color-accent) 12%, transparent)'}, color-mix(in srgb, var(--color-accent) 0%, transparent) 68%)`, pointerEvents: 'none' }} />

      <div className="max-w-7xl mx-auto relative">
        <div className="grid grid-cols-1 lg:grid-cols-[380px,minmax(0,1fr)] gap-12 lg:gap-16 items-start">

          {/* LEFT — pitch */}
          <Reveal>
            {tt(config.eyebrow) && (
              <div className="inline-flex items-center gap-2.5 mb-6" style={{ padding: '7px 16px 7px 7px', borderRadius: 999, background: pillBg, border: `1px solid ${pillBd}`, boxShadow: m.isDark ? 'none' : '0 1px 3px rgba(0,0,0,0.06)' }}>
                <span className="inline-flex items-center justify-center overflow-hidden" style={{ width: 30, height: 30, borderRadius: 999, background: m.isDark ? 'rgba(255,255,255,0.08)' : '#f5f7f4' }}>
                  {faviconUrl ? <img src={faviconUrl} alt="" style={{ width: 22, height: 'auto', display: 'block' }} /> : <span style={{ width: 12, height: 12, borderRadius: 999, background: accent }} />}
                </span>
                {tt(config.eyebrow) && <span style={{ fontSize: 12, letterSpacing: '0.18em', textTransform: 'uppercase', fontWeight: 600, color: m.isDark ? 'rgba(255,255,255,0.78)' : 'var(--accent-strong)' }}>{tt(config.eyebrow)}</span>}
              </div>
            )}
            {tt(config.title) && (
              <h2 data-testid="process-title" style={{ margin: 0, fontFamily: "'Sora', sans-serif", fontWeight: 800, fontSize: 'clamp(34px, 4vw, 50px)', lineHeight: 1.06, letterSpacing: '-0.02em', color: heading }}>
                {renderHeadline(tt(config.title))}
              </h2>
            )}
            {tt(config.subtitle) && <p style={{ margin: '22px 0 0', fontSize: 16.5, lineHeight: 1.7, color: muted, maxWidth: 340 }}>{tt(config.subtitle)}</p>}
          </Reveal>

          {/* RIGHT — vertical numbered rail */}
          <div className="relative" style={{ paddingTop: 4 }}>
            <div aria-hidden="true" style={{ position: 'absolute', left: 26, top: 30, bottom: 30, width: 2, background: railGrad, zIndex: 0 }} />
            {visibleItems.map((step, idx) => {
              const isLast = idx === visibleItems.length - 1;
              return (
                <Reveal key={step.id} delay={idx * 90} className="grid items-start" style={{ gridTemplateColumns: '54px minmax(0,1fr)', gap: 26, paddingBottom: isLast ? 0 : 40 }} data-testid={`process-step-${step.id}`}>
                  <span style={{ position: 'relative', zIndex: 2, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 54, height: 54, borderRadius: 999, fontFamily: "'Sora', sans-serif", fontWeight: 800, fontSize: 21, background: isLast ? (m.isDark ? '#FFFFFF' : '#1a2332') : accent, color: isLast ? accent : 'var(--accent-ink)' }}>
                    {step.step_number || (idx + 1)}
                  </span>
                  <div className="min-w-0" style={{ paddingTop: 4 }}>
                    <h3 style={{ margin: '8px 0 8px', fontFamily: "'Sora', sans-serif", fontSize: 21, fontWeight: 700, color: heading }}>{tt(step.title)}</h3>
                    {tt(step.description) && <div className="rich-text-content" style={{ fontSize: 15.5, lineHeight: 1.6, color: descColor, maxWidth: 480 }} dangerouslySetInnerHTML={{ __html: normalizeRichText(tt(step.description)) }} />}
                  </div>
                </Reveal>
              );
            })}
          </div>

        </div>
      </div>
    </section>
  );
}

// ─── Private Community Pro — "Latest News" (News) ──────────────────────────
// Option A "Featured + list": big featured lead (real cover) + a divided list
// of the next posts (each with its own thumbnail). Header = eyebrow pill w/
// favicon, mixed-weight headline, outline CTA. Real KMS images per post.
// Switched in HomePage for privatecommunity. Falls back to AurexNewsMono.
export function PcpNews({ posts, bg, font, cmsConfig = {}, favicon }) {
  const tt = useT();
  const { lang } = useLang();
  const filtered = (posts || []).filter(p => itemHasLocale(p.title, lang));
  if (!filtered.length) return null;
  const m = monoStyle(bg, font, '#FFFFFF');

  const accent   = 'var(--color-accent)';
  const heading  = m.isDark ? '#FFFFFF' : '#1a2332';
  const muted    = m.isDark ? 'rgba(255,255,255,0.66)' : '#5b6570';
  const snippet  = m.isDark ? 'rgba(255,255,255,0.6)'  : '#6b7580';
  const metaCol  = m.isDark ? 'rgba(255,255,255,0.5)'  : '#8a95a0';
  const divider  = m.isDark ? 'rgba(255,255,255,0.10)' : '#edf0ea';
  const pillBg   = m.isDark ? 'rgba(255,255,255,0.05)' : '#ffffff';
  const pillBd   = m.isDark ? 'rgba(255,255,255,0.12)' : 'color-mix(in srgb, var(--color-accent) 28%, transparent)';
  const faviconUrl = favicon ? resolveImg(favicon) : null;

  const renderHeadline = (title) => {
    const words = String(title || '').trim().split(/\s+/).filter(Boolean);
    if (words.length <= 2) return <>{title}</>;
    const first = words[0], last = words[words.length - 1], mid = words.slice(1, -1).join(' ');
    return (<>{first} <span style={{ fontFamily: "'Playfair Display', Georgia, serif", fontStyle: 'italic', fontWeight: 500, color: accent }}>{mid}</span> {last}</>);
  };
  const fmtDate = (d) => { try { return new Date(d).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }); } catch { return ''; } };
  const excerpt = (p) => (tt(p.summary) || tt(p.excerpt) || tt(p.content) || '').replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').replace(/ /g, ' ').trim();
  const chartFallback = (
    <svg viewBox="0 0 400 200" preserveAspectRatio="none" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}>
      <path d="M0 150 L55 118 L110 138 L165 88 L220 108 L275 58 L330 78 L400 36 L400 200 L0 200 Z" fill="color-mix(in srgb, var(--color-accent) 12%, transparent)" />
      <path d="M0 150 L55 118 L110 138 L165 88 L220 108 L275 58 L330 78 L400 36" fill="none" stroke="color-mix(in srgb, var(--color-accent) 70%, transparent)" strokeWidth="2.4" />
    </svg>
  );
  const DateChip = ({ d }) => (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontSize: 12, letterSpacing: '.06em', textTransform: 'uppercase', fontWeight: 600, color: metaCol }}>
      <svg width="13" height="13" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="9" stroke={metaCol} strokeWidth="1.7" /><path d="M12 7v5l3 2" stroke={metaCol} strokeWidth="1.7" strokeLinecap="round" /></svg>
      {fmtDate(d)}
    </span>
  );

  const featured = filtered[0];
  const rest = filtered.slice(1, 4);
  const ctaText = tt(cmsConfig.cta_text);

  return (
    <section className={monoShell} style={{ ...m.style, position: 'relative', overflow: 'hidden' }} id="news" data-testid="news-section">
      {!m.isDark && <div aria-hidden="true" style={{ position: 'absolute', top: -160, right: -150, width: 460, height: 460, borderRadius: 999, background: 'radial-gradient(circle, color-mix(in srgb, var(--color-accent) 10%, transparent), color-mix(in srgb, var(--color-accent) 0%, transparent) 70%)', pointerEvents: 'none' }} />}
      <div className="max-w-7xl mx-auto relative">

        {/* header */}
        <Reveal className="flex items-end justify-between mb-11 flex-wrap gap-6">
          <div>
            {tt(cmsConfig.eyebrow) && (
              <div className="inline-flex items-center gap-2.5 mb-5" style={{ padding: '7px 16px 7px 7px', borderRadius: 999, background: pillBg, border: `1px solid ${pillBd}`, boxShadow: m.isDark ? 'none' : '0 1px 3px rgba(0,0,0,0.06)' }}>
                <span className="inline-flex items-center justify-center overflow-hidden" style={{ width: 30, height: 30, borderRadius: 999, background: m.isDark ? 'rgba(255,255,255,0.08)' : '#f5f7f4' }}>
                  {faviconUrl ? <img src={faviconUrl} alt="" style={{ width: 22, height: 'auto', display: 'block' }} /> : <span style={{ width: 12, height: 12, borderRadius: 999, background: accent }} />}
                </span>
                {tt(cmsConfig.eyebrow) && <span style={{ fontSize: 12, letterSpacing: '0.18em', textTransform: 'uppercase', fontWeight: 600, color: m.isDark ? 'rgba(255,255,255,0.78)' : 'var(--accent-strong)' }}>{tt(cmsConfig.eyebrow)}</span>}
              </div>
            )}
            {tt(cmsConfig.title) && (
              <h2 data-testid="news-title" style={{ margin: 0, fontFamily: "'Sora', sans-serif", fontWeight: 800, fontSize: 'clamp(32px, 3.6vw, 46px)', lineHeight: 1.06, letterSpacing: '-0.02em', color: heading }}>
                {renderHeadline(tt(cmsConfig.title))}
              </h2>
            )}
          </div>
          {ctaText && (
            <PcpButton variant="link" dark={m.isDark} href={cmsConfig.cta_url} text={cmsConfig.cta_text} newTab={cmsConfig.cta_new_tab} dataTestId="news-view-all" />
          )}
        </Reveal>

        {/* featured + list */}
        <div className="grid grid-cols-1 lg:grid-cols-[1.35fr,1fr] gap-10 lg:gap-12 items-start">

          {/* FEATURED */}
          <Reveal as={Link} to={`/news/${featured.slug || featured.id}`} className="group block" style={{ color: 'inherit' }} data-testid="news-featured">
            <div style={{ position: 'relative', aspectRatio: '16/9', borderRadius: 18, overflow: 'hidden', background: 'linear-gradient(135deg,#17202e,#2b3a4d)' }}>
              {featured.image ? <img src={resolveImg(featured.image)} alt="" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" /> : chartFallback}
              <span style={{ position: 'absolute', top: 16, left: 16, display: 'inline-flex', alignItems: 'center', padding: '7px 13px', borderRadius: 999, background: 'color-mix(in srgb, var(--color-accent) 94%, transparent)', color: '#0f172a', fontSize: 11, fontWeight: 700, letterSpacing: '.1em', textTransform: 'uppercase' }}>Featured</span>
            </div>
            <div style={{ marginTop: 20 }}><DateChip d={featured.created_at} /></div>
            <h3 style={{ margin: '12px 0 12px', fontFamily: "'Sora', sans-serif", fontSize: 'clamp(22px, 2.2vw, 30px)', fontWeight: 700, lineHeight: 1.18, letterSpacing: '-.01em', color: heading }}>{tt(featured.title)}</h3>
            {excerpt(featured) && <p style={{ margin: '0 0 18px', fontSize: 16, lineHeight: 1.6, color: muted, maxWidth: 560, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{excerpt(featured)}</p>}
            <span className="pcp-readmore" style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 13, fontWeight: 700, letterSpacing: '.04em', textTransform: 'uppercase', color: m.isDark ? 'var(--accent-soft)' : 'var(--accent-strong)' }}>Read article <svg width="15" height="15" viewBox="0 0 24 24" fill="none" className="transition-transform duration-300 group-hover:translate-x-1"><path d="M5 12h14M13 6l6 6-6 6" stroke={m.isDark ? 'var(--accent-soft)' : 'var(--accent-strong)'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg></span>
          </Reveal>

          {/* LIST — each post with its thumbnail */}
          <div>
            {rest.map((p, idx) => (
              <Reveal as={Link} to={`/news/${p.slug || p.id}`} delay={idx * 80} key={p.id} className="group grid items-start" style={{ gridTemplateColumns: '116px minmax(0,1fr)', gap: 18, color: 'inherit', padding: '20px 0', borderTop: idx === 0 ? 'none' : `1px solid ${divider}` }} data-testid={`news-item-${p.id}`}>
                <div style={{ position: 'relative', aspectRatio: '16/11', borderRadius: 12, overflow: 'hidden', background: 'linear-gradient(135deg,#17202e,#2b3a4d)' }}>
                  {p.image ? <img src={resolveImg(p.image)} alt="" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" /> : chartFallback}
                </div>
                <div className="min-w-0">
                  <DateChip d={p.created_at} />
                  <h4 style={{ margin: '7px 0 0', fontFamily: "'Sora', sans-serif", fontSize: 17, fontWeight: 700, lineHeight: 1.3, color: heading, display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{tt(p.title)}</h4>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

// News
export function AurexNewsMono({ posts, bg, font, cmsConfig = {}, sectionNumber }) {
  const tt = useT();
  const { lang } = useLang();
  const filtered = (posts || []).filter(p => itemHasLocale(p.title, lang));
  if (!filtered.length) return null;
  const m = monoStyle(bg, font, '#FFFFFF');
  return (
    <section className={monoShell} style={m.style} id="news" data-testid="news-section">
      <div className="max-w-7xl mx-auto">
        <Reveal className="flex items-end justify-between mb-12 flex-wrap gap-4">
          <div>
            {(sectionNumber || tt(cmsConfig.eyebrow)) && (
              <p className={`text-[11px] uppercase tracking-[0.3em] font-semibold mb-3`} style={{ color: 'var(--color-primary)' }}>
                {sectionNumber && <span className="mr-1">{sectionNumber}/</span>}
                {tt(cmsConfig.eyebrow)}
              </p>
            )}
            {tt(cmsConfig.title) && <h2 className="text-3xl md:text-5xl font-bold tracking-tight" data-testid="news-title">{tt(cmsConfig.title)}</h2>}
            {tt(cmsConfig.subtitle) && <p className={`mt-2 max-w-2xl ${m.mutedClass}`}>{tt(cmsConfig.subtitle)}</p>}
          </div>
          {tt(cmsConfig.cta_text) && <MonoButton href={cmsConfig.cta_url} text={cmsConfig.cta_text} newTab={cmsConfig.cta_new_tab} m={m} dataTestId="news-view-all" />}
        </Reveal>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {filtered.slice(0, 3).map((p, idx) => (
            <Reveal as={Link} delay={idx * 80} to={`/news/${p.slug || p.id}`} key={p.id} className="group rounded-xl overflow-hidden transition-colors block" style={{ borderWidth: 1, borderStyle: 'solid', borderColor: m.borderColor }}>
              {p.image && <div className="aspect-[16/10] overflow-hidden" style={{ backgroundColor: m.isDark ? 'rgba(255,255,255,0.05)' : '#F9FAFB' }}><img src={resolveImg(p.image)} alt="" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" style={{ filter: 'grayscale(10%)' }} /></div>}
              <div className="p-6">
                <div className="flex items-center gap-2 mb-3"><Clock className={`w-3 h-3 ${m.eyebrowClass}`} /><span className={`text-[11px] uppercase tracking-wider ${m.eyebrowClass}`}>{new Date(p.created_at).toLocaleDateString()}</span></div>
                <h3 className="font-semibold text-lg mb-2 line-clamp-2">{tt(p.title)}</h3>
                {/* Render the admin's rich-text summary. Fall back to a
                    stripped-HTML excerpt from the article body only when
                    the admin hasn't filled the summary. */}
                {tt(p.summary)
                  ? <div className={`text-sm ${m.mutedClass} line-clamp-2 rich-text-content`} dangerouslySetInnerHTML={{ __html: normalizeRichText(tt(p.summary)) }} />
                  : (tt(p.excerpt) || tt(p.content))
                    ? <p className={`text-sm ${m.mutedClass} line-clamp-2`}>{(tt(p.excerpt) || tt(p.content) || '').replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').replace(/\u00A0/g, ' ').slice(0, 120)}</p>
                    : null}
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── Private Community Pro — "Insights" (external blog / KMS) ───────────────
// Option B "Elevated cards": PCP header (eyebrow pill w/ favicon, mixed-weight
// headline, outline CTA) + per-category groups, each a labelled row of elevated
// cards (real cover image, title, summary, external-link). Keeps AurexBlogMono's
// self-fetching (flat or category-grouped). Switched in HomePage for privatecommunity.
export function PcpBlog({ bg, font, cmsConfig = {}, favicon }) {
  const tt = useT();
  const { lang } = useLang();
  const settings = useSettings();
  const m = monoStyle(bg, font, '#F7F8F9');
  const PER = 3;

  const accent   = 'var(--color-accent)';
  const heading  = m.isDark ? '#FFFFFF' : '#1a2332';
  const snippet  = m.isDark ? 'rgba(255,255,255,0.62)' : '#6b7580';
  const cardBg   = m.isDark ? 'rgba(255,255,255,0.04)' : '#ffffff';
  const cardBd   = m.isDark ? 'rgba(255,255,255,0.09)' : '#e7ebe4';
  const pillBg   = m.isDark ? 'rgba(255,255,255,0.05)' : '#ffffff';
  const pillBd   = m.isDark ? 'rgba(255,255,255,0.12)' : 'color-mix(in srgb, var(--color-accent) 28%, transparent)';
  const catCol   = m.isDark ? 'rgba(255,255,255,0.55)' : '#8a95a0';
  const faviconUrl = favicon ? resolveImg(favicon) : null;

  const renderHeadline = (title) => {
    const words = String(title || '').trim().split(/\s+/).filter(Boolean);
    if (words.length <= 2) return <>{title}</>;
    const first = words[0], last = words[words.length - 1], mid = words.slice(1, -1).join(' ');
    return (<>{first} <span style={{ fontFamily: "'Playfair Display', Georgia, serif", fontStyle: 'italic', fontWeight: 500, color: accent }}>{mid}</span> {last}</>);
  };

  // Per-block KMS category selection (mirrors AurexBlogMono): grouped when the
  // admin picked categories, else a flat "latest N" feed.
  const catCfg = (Array.isArray(cmsConfig.kms_categories) ? cmsConfig.kms_categories : []).filter(c => c && c.slug);
  const slugs = catCfg.map(c => c.slug).join(',');
  const [posts, setPosts] = useState([]);
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    if (!settings.blog_api_url) { setLoading(false); return; }
    setLoading(true);
    let cancelled = false;
    const settle = () => { if (!cancelled) setLoading(false); };
    if (slugs) {
      blogExternalAPI.getLatest(slugs, PER).then(r => { if (!cancelled) setSections(r.data?.sections || []); }).catch(() => {}).finally(settle);
    } else {
      blogExternalAPI.getLatest().then(r => { if (!cancelled) setPosts(r.data?.posts || []); }).catch(() => {}).finally(settle);
    }
    return () => { cancelled = true; };
  }, [settings.blog_api_url, slugs]);

  const labelFor = (s) => {
    const cfg = catCfg.find(c => c.slug === s.slug);
    return (cfg && cfg.label && tt(cfg.label)) || s.label || s.name || '';
  };
  const visible = (arr) => (arr || []).filter(p => itemHasLocale(p.title, lang));
  const grouped = slugs ? sections.map(s => ({ ...s, _posts: visible(s.posts).slice(0, PER) })).filter(s => s._posts.length) : [];
  const flat = slugs ? [] : visible(posts).slice(0, PER);
  const reserving = loading && !!slugs;
  if (!reserving && (slugs ? !grouped.length : !flat.length)) return null;

  const chartFallback = (
    <svg viewBox="0 0 400 200" preserveAspectRatio="none" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}>
      <path d="M0 120 L55 140 L110 96 L165 126 L220 84 L275 108 L330 66 L400 92 L400 200 L0 200 Z" fill="color-mix(in srgb, var(--color-accent) 12%, transparent)" />
      <path d="M0 120 L55 140 L110 96 L165 126 L220 84 L275 108 L330 66 L400 92" fill="none" stroke="color-mix(in srgb, var(--color-accent) 70%, transparent)" strokeWidth="2.4" />
    </svg>
  );
  const card = (p, i) => (
    <Reveal as="a" href={p.url || p.link} target="_blank" rel="noreferrer" delay={i * 80} key={i} className="group flex flex-col rounded-2xl overflow-hidden" style={{ background: cardBg, border: `1px solid ${cardBd}`, boxShadow: m.isDark ? 'none' : '0 22px 55px -44px rgba(26,35,50,.35)', color: 'inherit' }} data-testid="insights-card">
      <div style={{ position: 'relative', aspectRatio: '16/10', background: 'linear-gradient(135deg,#17202e,#2b3a4d)' }}>
        {p.image ? <img src={p.image} alt="" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" /> : chartFallback}
      </div>
      <div className="flex flex-col flex-1" style={{ padding: '24px 24px 26px' }}>
        <h3 style={{ margin: '0 0 10px', fontFamily: "'Sora', sans-serif", fontSize: 18, fontWeight: 700, lineHeight: 1.32, color: heading, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{p.title}</h3>
        {(p.summary || p.excerpt) && <p style={{ margin: '0 0 18px', fontSize: 14, lineHeight: 1.55, color: snippet, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{p.summary || p.excerpt}</p>}
        <span className="pcp-readmore" style={{ marginTop: 'auto', display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 12.5, fontWeight: 700, letterSpacing: '.05em', textTransform: 'uppercase', color: m.isDark ? 'var(--accent-soft)' : 'var(--accent-strong)' }}>
          Read article <ExternalLink className="w-3.5 h-3.5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
        </span>
      </div>
    </Reveal>
  );

  const reservedRows = Math.max(1, catCfg.length);
  const reservation = (
    <div className="flex flex-col gap-14" style={{ visibility: 'hidden' }} aria-hidden="true" data-testid="insights-reservation">
      {Array.from({ length: reservedRows }, (_, r) => (
        <div key={r}>
          <div style={{ height: 20, marginBottom: 20 }} />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {Array.from({ length: 3 }, (_, i) => (
              <div key={i} className="rounded-2xl overflow-hidden" style={{ background: cardBg, border: `1px solid ${cardBd}` }}>
                <div style={{ aspectRatio: '16/10' }} />
                <div style={{ padding: '24px' }}><h3 className="font-semibold text-lg">&nbsp;<br />&nbsp;</h3><p>&nbsp;<br />&nbsp;</p></div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );

  const ctaText = tt(cmsConfig.cta_text);

  const groupHeader = (label, url) => (
    <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
      <div style={{ display: 'inline-flex', alignItems: 'center', gap: 12 }}>
        <span style={{ width: 22, height: 2, borderRadius: 2, background: accent }} />
        <span style={{ fontSize: 13, fontWeight: 700, letterSpacing: '0.16em', textTransform: 'uppercase', color: catCol }}>{label}</span>
      </div>
      {url && (
        <a href={url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 hover:gap-2.5 transition-all" style={{ color: m.isDark ? 'var(--accent-soft)' : 'var(--accent-strong)', fontSize: 13, fontWeight: 700 }}>
          View all <ArrowRight className="w-4 h-4" />
        </a>
      )}
    </div>
  );

  return (
    <section className={monoShell} style={{ ...m.style, position: 'relative', overflow: 'hidden' }} id="blog" data-testid="blog-section">
      {!m.isDark && <div aria-hidden="true" style={{ position: 'absolute', top: -160, right: -150, width: 460, height: 460, borderRadius: 999, background: 'radial-gradient(circle, color-mix(in srgb, var(--color-accent) 10%, transparent), color-mix(in srgb, var(--color-accent) 0%, transparent) 70%)', pointerEvents: 'none' }} />}
      <div className="max-w-7xl mx-auto relative">

        {/* header */}
        <Reveal className="flex items-end justify-between mb-11 flex-wrap gap-6">
          <div>
            {tt(cmsConfig.eyebrow) && (
              <div className="inline-flex items-center gap-2.5 mb-5" style={{ padding: '7px 16px 7px 7px', borderRadius: 999, background: pillBg, border: `1px solid ${pillBd}`, boxShadow: m.isDark ? 'none' : '0 1px 3px rgba(0,0,0,0.06)' }}>
                <span className="inline-flex items-center justify-center overflow-hidden" style={{ width: 30, height: 30, borderRadius: 999, background: m.isDark ? 'rgba(255,255,255,0.08)' : '#f5f7f4' }}>
                  {faviconUrl ? <img src={faviconUrl} alt="" style={{ width: 22, height: 'auto', display: 'block' }} /> : <span style={{ width: 12, height: 12, borderRadius: 999, background: accent }} />}
                </span>
                {tt(cmsConfig.eyebrow) && <span style={{ fontSize: 12, letterSpacing: '0.18em', textTransform: 'uppercase', fontWeight: 600, color: m.isDark ? 'rgba(255,255,255,0.78)' : 'var(--accent-strong)' }}>{tt(cmsConfig.eyebrow)}</span>}
              </div>
            )}
            {tt(cmsConfig.title) && (
              <h2 data-testid="blog-title" style={{ margin: 0, fontFamily: "'Sora', sans-serif", fontWeight: 800, fontSize: 'clamp(32px, 3.6vw, 46px)', lineHeight: 1.06, letterSpacing: '-0.02em', color: heading }}>
                {renderHeadline(tt(cmsConfig.title))}
              </h2>
            )}
          </div>
          {ctaText && (
            <PcpButton variant="link" dark={m.isDark} href={cmsConfig.cta_url} text={cmsConfig.cta_text} newTab={cmsConfig.cta_new_tab} dataTestId="blog-view-all" />
          )}
        </Reveal>

        {reserving ? reservation : slugs ? (
          <div className="flex flex-col gap-14">
            {grouped.map((s, si) => (
              <div key={s.slug || si} data-testid={`insights-cat-${s.slug}`}>
                {groupHeader(labelFor(s), s.url)}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {s._posts.map((p, i) => card(p, i))}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {flat.map((p, i) => card(p, i))}
          </div>
        )}
      </div>
    </section>
  );
}

// External Blog
export function AurexBlogMono({ bg, font, cmsConfig = {}, sectionNumber }) {
  const tt = useT();
  const { lang } = useLang();
  const settings = useSettings();
  const m = monoStyle(bg, font, '#F9FAFB');
  const PER = 3;
  // Per-block KMS category selection (EXTERNAL_BLOG_KMS_SECTIONS_PLAN §2.5):
  // when the admin has picked categories, render one labelled section per
  // category; otherwise keep the legacy flat "latest N" feed.
  const catCfg = (Array.isArray(cmsConfig.kms_categories) ? cmsConfig.kms_categories : [])
    .filter(c => c && c.slug);
  const slugs = catCfg.map(c => c.slug).join(',');
  const [posts, setPosts] = useState([]);
  const [sections, setSections] = useState([]);
  // This block fetches its own feed AFTER it mounts, i.e. after HomePage has already
  // revealed the page (measured: reveal ~1.4s, feed ~3.5s). Without a reservation the
  // whole section pops into the flow at full height and shoves everything below it
  // down — measured at 1333px on a cold load. `loading` distinguishes "not here yet"
  // (reserve the space) from "loaded and genuinely empty" (render nothing, as before).
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    if (!settings.blog_api_url) { setLoading(false); return; }
    setLoading(true);
    let cancelled = false;
    const settle = () => { if (!cancelled) setLoading(false); };
    if (slugs) {
      blogExternalAPI.getLatest(slugs, PER)
        .then(r => { if (!cancelled) setSections(r.data?.sections || []); })
        .catch(() => {}).finally(settle);
    } else {
      blogExternalAPI.getLatest()
        .then(r => { if (!cancelled) setPosts(r.data?.posts || []); })
        .catch(() => {}).finally(settle);
    }
    return () => { cancelled = true; };
  }, [settings.blog_api_url, slugs]);

  // Optional per-category label override from the block config; else the KMS name.
  const labelFor = (s) => {
    const cfg = catCfg.find(c => c.slug === s.slug);
    return (cfg && cfg.label && tt(cfg.label)) || s.label || s.name || '';
  };

  // External blog posts come from a 3rd-party source — they're plain-string
  // titles. `itemHasLocale` treats plain strings as visible in all locales.
  const visible = (arr) => (arr || []).filter(p => itemHasLocale(p.title, lang));
  const card = (p, i) => (
    <Reveal as="a" href={p.url || p.link} target="_blank" rel="noreferrer" delay={i * 80} key={i} className="group rounded-xl overflow-hidden transition-colors block" style={{ backgroundColor: m.isDark ? 'rgba(255,255,255,0.04)' : '#FFFFFF', borderWidth: 1, borderStyle: 'solid', borderColor: m.borderColor }}>
      {p.image && <div className="aspect-[16/10] overflow-hidden"><img src={p.image} alt="" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" style={{ filter: 'grayscale(10%)' }} /></div>}
      <div className="p-6">
        <h3 className="font-semibold text-lg mb-2 flex items-center gap-1 line-clamp-2">{p.title} <ExternalLink className="w-3 h-3 flex-shrink-0 opacity-40" /></h3>
        <p className={`text-sm ${m.mutedClass} line-clamp-2`}>{p.summary || p.excerpt}</p>
      </div>
    </Reveal>
  );

  const grouped = slugs
    ? sections.map(s => ({ ...s, _posts: visible(s.posts).slice(0, PER) })).filter(s => s._posts.length)
    : [];
  const flat = slugs ? [] : visible(posts).slice(0, PER);
  // Only reserve when the admin has explicitly picked categories in the CMS — that
  // is a reliable statement that this block WILL have content. In the legacy flat
  // mode there is no such signal, and reserving space for a feed that comes back
  // empty would collapse the section afterwards: a jump upwards where there was
  // none before. This component is shared by all three brands, so the conservative
  // branch keeps their behaviour byte-for-byte unchanged.
  const reserving = loading && !!slugs;
  if (!reserving && (slugs ? !grouped.length : !flat.length)) return null;

  // Space held for the feed while it is in flight. Built from the real card box
  // model (same classes, same type sizes) rather than guessed pixel heights, and
  // visibility:hidden so it reserves layout without drawing anything — the cards
  // then appear in place instead of pushing the page down.
  // Row count is exact: each category group renders ONE row of up to 3 cards, so
  // the reservation is right whether a group comes back with 1, 2 or 3 posts.
  const reservedRows = Math.max(1, catCfg.length);
  const reservedCard = (i) => (
    <div key={i} className="rounded-xl overflow-hidden" style={{ backgroundColor: m.isDark ? 'rgba(255,255,255,0.04)' : '#FFFFFF', borderWidth: 1, borderStyle: 'solid', borderColor: m.borderColor }}>
      <div className="aspect-[16/10]" />
      <div className="p-6">
        <h3 className="font-semibold text-lg mb-2">&nbsp;<br />&nbsp;</h3>
        <p className="text-sm">&nbsp;<br />&nbsp;</p>
      </div>
    </div>
  );
  const reservation = (
    <div className="space-y-14" style={{ visibility: 'hidden' }} aria-hidden="true" data-testid="blog-reservation">
      {Array.from({ length: reservedRows }, (_, r) => (
        <div key={r}>
          <div className="flex items-end justify-between mb-6 flex-wrap gap-3"><h3 className="text-xl md:text-2xl font-bold tracking-tight">&nbsp;</h3></div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {Array.from({ length: 3 }, (_, i) => reservedCard(i))}
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <section className={monoShell} style={m.style} id="blog" data-testid="blog-section">
      <div className="max-w-7xl mx-auto">
        <Reveal className="flex items-end justify-between mb-12 flex-wrap gap-4">
          <div>
            {(sectionNumber || tt(cmsConfig.eyebrow)) && (
              <p className={`text-[11px] uppercase tracking-[0.3em] font-semibold mb-3`} style={{ color: 'var(--color-primary)' }}>
                {sectionNumber && <span className="mr-1">{sectionNumber}/</span>}
                {tt(cmsConfig.eyebrow)}
              </p>
            )}
            {tt(cmsConfig.title) && <h2 className="text-3xl md:text-5xl font-bold tracking-tight" data-testid="blog-title">{tt(cmsConfig.title)}</h2>}
            {tt(cmsConfig.subtitle) && <p className={`mt-2 max-w-2xl ${m.mutedClass}`}>{tt(cmsConfig.subtitle)}</p>}
          </div>
          {tt(cmsConfig.cta_text) && <MonoButton href={cmsConfig.cta_url} text={cmsConfig.cta_text} newTab={cmsConfig.cta_new_tab} m={m} dataTestId="blog-view-all" />}
        </Reveal>
        {reserving ? reservation : slugs ? (
          <div className="space-y-14">
            {grouped.map((s, si) => (
              <div key={s.slug || si} data-testid={`blog-cat-${s.slug}`}>
                <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
                  <h3 className="text-xl md:text-2xl font-bold tracking-tight">{labelFor(s)}</h3>
                  {s.url && (
                    <a href={s.url} target="_blank" rel="noreferrer" className="text-sm font-medium inline-flex items-center gap-1 hover:gap-2 transition-all py-3 -my-3" style={{ color: 'var(--color-primary)' }} data-testid={`blog-cat-viewall-${s.slug}`}>
                      View all <ArrowRight className="w-4 h-4" />
                    </a>
                  )}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {s._posts.map((p, i) => card(p, i))}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {flat.map((p, i) => card(p, i))}
          </div>
        )}
      </div>
    </section>
  );
}

// Reading List
export function AurexReadingMono({ books, bg, font, cmsConfig = {}, favicon }) {
  const tt = useT();
  const { lang } = useLang();
  const filtered = (books || []).filter(b => itemHasLocale(b.title, lang));
  if (!filtered.length) return null;
  const m = monoStyle(bg, font, '#FFFFFF');
  // Breadcrumb pill w/ brand favicon — same treatment as PcpTeam so the Reading
  // List section header matches "Our Team".
  const faviconUrl = favicon ? resolveImg(favicon) : null;
  const accent     = 'var(--color-accent)';
  const pillBg     = m.isDark ? 'rgba(255,255,255,0.05)' : '#ffffff';
  const pillBd     = m.isDark ? 'rgba(255,255,255,0.12)' : 'color-mix(in srgb, var(--color-accent) 28%, transparent)';
  const pillIcoBg  = m.isDark ? 'rgba(255,255,255,0.08)' : '#f5f7f4';
  const eyebrowCol = m.isDark ? 'rgba(255,255,255,0.8)' : 'var(--accent-strong)';
  return (
    <section className={monoShell} style={m.style} id="reading-list" data-testid="reading-section">
      <div className="max-w-7xl mx-auto">
        <Reveal className="flex items-end justify-between mb-12 flex-wrap gap-4">
          <div>
            <div className="inline-flex items-center gap-2.5 mb-5" style={{ padding: '7px 16px 7px 7px', borderRadius: 999, background: pillBg, border: `1px solid ${pillBd}`, boxShadow: m.isDark ? 'none' : '0 1px 3px rgba(0,0,0,0.06)' }}>
              <span className="inline-flex items-center justify-center overflow-hidden" style={{ width: 30, height: 30, borderRadius: 999, background: pillIcoBg }}>
                {faviconUrl ? <img src={faviconUrl} alt="" style={{ width: 22, height: 'auto', display: 'block' }} /> : <span style={{ width: 12, height: 12, borderRadius: 999, background: accent }} />}
              </span>
              <span style={{ fontSize: 12, letterSpacing: '0.18em', textTransform: 'uppercase', fontWeight: 600, color: eyebrowCol }}>{tt(cmsConfig.eyebrow) || 'Reading'}</span>
            </div>
            <h2 className="text-3xl md:text-5xl font-bold tracking-tight" data-testid="reading-title">{tt(cmsConfig.title) || 'Reading List'}</h2>
            {tt(cmsConfig.subtitle) && <p className={`mt-3 ${m.mutedClass}`}>{tt(cmsConfig.subtitle)}</p>}
          </div>
          <Link to={tt(cmsConfig.cta_url) || '/reading-list'} target={cmsConfig.cta_new_tab ? '_blank' : '_self'} rel="noopener noreferrer" className="pcpb-link" data-contrast={m.isDark ? 'dark' : 'light'}>{tt(cmsConfig.cta_text) || 'View all'} <ArrowRight className="w-4 h-4" /></Link>
        </Reveal>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-6">
          {filtered.slice(0, 5).map((b, idx) => {
            const cover = b.image || b.cover_image;
            const src = resolveImg(cover);
            return (
              <Reveal as={Link} to="/reading-list" delay={idx * 60} key={b.id} className="group block transition-all duration-300 hover:-translate-y-1.5">
                {src ? <div className="relative aspect-[2/3] overflow-hidden rounded-xl shadow-md transition-shadow duration-300 group-hover:shadow-2xl" style={{ backgroundColor: m.isDark ? 'rgba(255,255,255,0.04)' : '#F9FAFB' }}><img src={src} alt={tt(b.title)} className="w-full h-full object-cover transition-transform duration-[600ms] ease-out group-hover:scale-[1.08]" /><span aria-hidden="true" className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300" style={{ background: 'linear-gradient(to top, rgba(15,23,42,.45), rgba(15,23,42,0) 55%)' }} /></div>
                     : <div className="aspect-[2/3] flex items-center justify-center bg-gray-900 rounded-xl"><BookOpen className="w-8 h-8 text-white/60" /></div>}
                <div className="pt-3">
                  <p className="text-sm font-semibold leading-snug">{tt(b.title)}</p>
                  <p className={`text-xs ${m.mutedClass} truncate`}>{tt(b.author)}</p>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// ─── Model Portfolio (Aurex family) ─────────────────────────────────────────
// Logo wall of the companies in the most recent iiReport (KMS Portfolio &
// Rankings), styled in the Aurex mono language (favicon pill eyebrow + headline
// + subtitle). Data: GET /api/public/model-portfolio (computed on read → always
// the latest report). Every logo links to that report's KMS post; the shared SSO
// link interceptor upgrades the click for members. Company logos come from our
// own companies directory.
export function AurexModelPortfolioMono({ bg, font, cmsConfig = {}, favicon }) {
  const tt = useT();
  const [data, setData] = useState(null);
  useEffect(() => {
    let alive = true;
    fetch(`${API}/api/public/model-portfolio`)
      .then(r => r.json())
      .then(d => { if (alive) setData(d); })
      .catch(() => { if (alive) setData({ enabled: false, companies: [] }); });
    return () => { alive = false; };
  }, []);
  const companies = (data && data.enabled && data.companies) || [];
  if (!companies.length) return null;
  const postUrl = data.post_url || '#';
  const m = monoStyle(bg, font, '#FFFFFF');
  const faviconUrl = favicon ? resolveImg(favicon) : null;
  const accent     = 'var(--color-accent)';
  const pillBg     = m.isDark ? 'rgba(255,255,255,0.05)' : '#ffffff';
  const pillBd     = m.isDark ? 'rgba(255,255,255,0.12)' : 'color-mix(in srgb, var(--color-accent) 28%, transparent)';
  const pillIcoBg  = m.isDark ? 'rgba(255,255,255,0.08)' : '#f5f7f4';
  const eyebrowCol = m.isDark ? 'rgba(255,255,255,0.8)' : 'var(--accent-strong)';
  const cardBd     = m.isDark ? 'rgba(255,255,255,0.14)' : '#e5e7eb';
  return (
    <section className={monoShell} style={m.style} id="model-portfolio" data-testid="model-portfolio-section">
      <div className="max-w-7xl mx-auto">
        <Reveal className="mb-12">
          <div className="inline-flex items-center gap-2.5 mb-5" style={{ padding: '7px 16px 7px 7px', borderRadius: 999, background: pillBg, border: `1px solid ${pillBd}`, boxShadow: m.isDark ? 'none' : '0 1px 3px rgba(0,0,0,0.06)' }}>
            <span className="inline-flex items-center justify-center overflow-hidden" style={{ width: 30, height: 30, borderRadius: 999, background: pillIcoBg }}>
              {faviconUrl ? <img src={faviconUrl} alt="" style={{ width: 22, height: 'auto', display: 'block' }} /> : <span style={{ width: 12, height: 12, borderRadius: 999, background: accent }} />}
            </span>
            <span style={{ fontSize: 12, letterSpacing: '0.18em', textTransform: 'uppercase', fontWeight: 600, color: eyebrowCol }}>{tt(cmsConfig.eyebrow) || 'Model Portfolio'}</span>
          </div>
          <h2 className="text-3xl md:text-5xl font-bold tracking-tight" data-testid="model-portfolio-title">{tt(cmsConfig.title) || 'Model Portfolio'}</h2>
          {tt(cmsConfig.subtitle) && <p className={`mt-3 ${m.mutedClass}`}>{tt(cmsConfig.subtitle)}</p>}
        </Reveal>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
          {companies.map(c => (
            <a
              key={c.symbol}
              href={postUrl}
              title={c.name}
              aria-label={c.name}
              data-testid={`mp-logo-${c.symbol}`}
              className="group flex items-center justify-center rounded-xl transition-all duration-200 hover:-translate-y-0.5"
              style={{ border: `1px solid ${cardBd}`, backgroundColor: '#ffffff', padding: '22px 16px', minHeight: 96 }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = accent; e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.08)'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = cardBd; e.currentTarget.style.boxShadow = 'none'; }}
            >
              <img src={resolveImg(c.logo)} alt={c.name} loading="lazy" style={{ maxHeight: 44, maxWidth: '100%', width: 'auto', objectFit: 'contain' }} />
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}

// Map
// ─── Private Community Pro — "Our offices" (Option F: map + directory) ──────
// Header (eyebrow pill w/ favicon, mixed-weight headline, subtitle) + a split:
// the REAL interactive Leaflet map on the left, an office directory on the right
// (name + description, links out when set). Dedupes exact-duplicate markers.
// Switched in HomePage for privatecommunity. Falls back to AurexMapMono otherwise.
export function PcpMap({ maps, locations, title, mapsLang, bg, font, cmsConfig = {}, favicon }) {
  const tt = useT();
  const raw = [...(maps || []).filter(m => m.lat && m.lng), ...(locations || []).filter(l => l.lat && l.lng)];
  // Drop exact duplicates (same name + coordinates) so markers don't stack and
  // the directory doesn't repeat — the CMS currently holds test copies.
  const seen = new Set();
  const all = raw.filter(l => {
    const key = `${String(tt(l.title) || l.name || '').toLowerCase()}|${l.lat}|${l.lng}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
  if (!all.length) return null;
  const m = monoStyle(bg, font, '#F7F8F9');
  const center = [all[0].lat, all[0].lng];
  const lang = mapsLang || 'local';

  const accent   = 'var(--color-accent)';
  const heading  = m.isDark ? '#FFFFFF' : '#1a2332';
  const muted    = m.isDark ? 'rgba(255,255,255,0.66)' : '#5b6570';
  const rowDesc  = m.isDark ? 'rgba(255,255,255,0.6)' : '#6b7580';
  const divider  = m.isDark ? 'rgba(255,255,255,0.10)' : '#edf0ea';
  const chipBg   = m.isDark ? 'color-mix(in srgb, var(--color-accent) 16%, transparent)' : 'color-mix(in srgb, var(--color-accent) 12%, transparent)';
  const chipIco  = m.isDark ? 'var(--color-accent)' : 'var(--accent-strong)';
  const pillBg   = m.isDark ? 'rgba(255,255,255,0.05)' : '#ffffff';
  const pillBd   = m.isDark ? 'rgba(255,255,255,0.12)' : 'color-mix(in srgb, var(--color-accent) 28%, transparent)';
  const mapBd    = m.isDark ? 'rgba(255,255,255,0.10)' : '#e2e7df';
  const faviconUrl = favicon ? resolveImg(favicon) : null;

  const renderHeadline = (t) => {
    const words = String(t || '').trim().split(/\s+/).filter(Boolean);
    if (words.length <= 2) return <>{t}</>;
    const first = words[0], last = words[words.length - 1], mid = words.slice(1, -1).join(' ');
    return (<>{first} <span style={{ fontFamily: "'Playfair Display', Georgia, serif", fontStyle: 'italic', fontWeight: 500, color: accent }}>{mid}</span> {last}</>);
  };

  return (
    <section className={monoShell} style={{ ...m.style, position: 'relative', overflow: 'hidden' }} id="locations" data-testid="map-section">
      {!m.isDark && <div aria-hidden="true" style={{ position: 'absolute', top: -160, left: -150, width: 460, height: 460, borderRadius: 999, background: 'radial-gradient(circle, color-mix(in srgb, var(--color-accent) 9%, transparent), color-mix(in srgb, var(--color-accent) 0%, transparent) 70%)', pointerEvents: 'none' }} />}
      <div className="max-w-7xl mx-auto relative">
        <Reveal style={{ marginBottom: 34 }}>
          {tt(cmsConfig.eyebrow) && (
            <div className="inline-flex items-center gap-2.5 mb-5" style={{ padding: '7px 16px 7px 7px', borderRadius: 999, background: pillBg, border: `1px solid ${pillBd}`, boxShadow: m.isDark ? 'none' : '0 1px 3px rgba(0,0,0,0.06)' }}>
              <span className="inline-flex items-center justify-center overflow-hidden" style={{ width: 30, height: 30, borderRadius: 999, background: m.isDark ? 'rgba(255,255,255,0.08)' : '#f5f7f4' }}>
                {faviconUrl ? <img src={faviconUrl} alt="" style={{ width: 22, height: 'auto', display: 'block' }} /> : <span style={{ width: 12, height: 12, borderRadius: 999, background: accent }} />}
              </span>
              {tt(cmsConfig.eyebrow) && <span style={{ fontSize: 12, letterSpacing: '0.18em', textTransform: 'uppercase', fontWeight: 600, color: m.isDark ? 'rgba(255,255,255,0.78)' : 'var(--accent-strong)' }}>{tt(cmsConfig.eyebrow)}</span>}
            </div>
          )}
          {(tt(cmsConfig.title) || tt(title)) && (
            <h2 data-testid="map-title" style={{ margin: '0 0 8px', fontFamily: "'Sora', sans-serif", fontWeight: 800, fontSize: 'clamp(32px, 3.6vw, 46px)', lineHeight: 1.06, letterSpacing: '-0.02em', color: heading }}>
              {renderHeadline(tt(cmsConfig.title) || tt(title))}
            </h2>
          )}
          {tt(cmsConfig.subtitle) && <p style={{ margin: 0, fontSize: 16, lineHeight: 1.65, color: muted, maxWidth: 560 }}>{tt(cmsConfig.subtitle)}</p>}
        </Reveal>

        <div className="grid grid-cols-1 lg:grid-cols-[1.5fr,1fr] gap-10 lg:gap-12 items-start">
          {/* interactive map */}
          <Reveal className="rounded-2xl overflow-hidden" style={{ border: `1px solid ${mapBd}`, height: 460 }}>
            <MapContainer center={center} zoom={2} scrollWheelZoom={false} style={{ height: '100%', width: '100%' }}>
              <TileLayer url={getTileUrl(lang)} attribution={getTileAttribution(lang)} />
              <MarkerClusterGroup>
                {all.map((loc, i) => (
                  <Marker key={i} position={[loc.lat, loc.lng]}>
                    <Popup><strong>{tt(loc.title) || loc.name}</strong>{loc.description && <p>{loc.description}</p>}{loc.link && <a href={loc.link} target={loc.open_in_new_tab ? '_blank' : '_self'} rel="noreferrer">Visit</a>}</Popup>
                  </Marker>
                ))}
              </MarkerClusterGroup>
            </MapContainer>
          </Reveal>

          {/* office directory */}
          <div className="flex flex-col">
            {all.map((loc, i) => {
              const Wrap = loc.link ? 'a' : 'div';
              return (
                <Wrap key={i} href={loc.link || undefined} target={loc.link && loc.open_in_new_tab ? '_blank' : undefined} rel={loc.link && loc.open_in_new_tab ? 'noreferrer' : undefined}
                  className="flex items-start gap-4" style={{ padding: '16px 0', borderTop: i === 0 ? 'none' : `1px solid ${divider}`, color: 'inherit', textDecoration: 'none' }} data-testid={`office-${i}`}>
                  <span className="inline-flex items-center justify-center flex-none" style={{ width: 38, height: 38, borderRadius: 11, background: chipBg, color: chipIco }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M12 21s7-5.5 7-11a7 7 0 1 0-14 0c0 5.5 7 11 7 11Z" stroke="currentColor" strokeWidth="1.7" /><circle cx="12" cy="10" r="2.4" stroke="currentColor" strokeWidth="1.7" /></svg>
                  </span>
                  <div className="min-w-0">
                    <h4 style={{ margin: '0 0 2px', fontFamily: "'Sora', sans-serif", fontSize: 15.5, fontWeight: 700, color: heading }}>{tt(loc.title) || loc.name}</h4>
                    {loc.description && <p style={{ margin: 0, fontSize: 13, lineHeight: 1.45, color: rowDesc }}>{loc.description}</p>}
                  </div>
                </Wrap>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}

export function AurexMapMono({ maps, locations, title, mapsLang, bg, font, cmsConfig = {} }) {
  const tt = useT();
  const all = [...(maps || []).filter(m => m.lat && m.lng), ...(locations || []).filter(l => l.lat && l.lng)];
  if (!all.length) return null;
  const m = monoStyle(bg, font, '#F4F6F8');
  const center = [all[0].lat, all[0].lng];
  const lang = mapsLang || 'local';
  return (
    <section className={monoShell} style={m.style} id="locations" data-testid="map-section">
      <div className="max-w-7xl mx-auto">
        <Reveal className="text-center mb-12">
          {tt(cmsConfig.eyebrow) && <p className={`text-[11px] uppercase tracking-[0.3em] font-semibold ${m.eyebrowClass} mb-3`}>{tt(cmsConfig.eyebrow)}</p>}
          {(tt(cmsConfig.title) || tt(title)) && <h2 className="text-3xl md:text-5xl font-bold tracking-tight" data-testid="map-title">{tt(cmsConfig.title) || tt(title)}</h2>}
          {tt(cmsConfig.subtitle) && <p className={`mt-3 max-w-2xl mx-auto ${m.mutedClass}`}>{tt(cmsConfig.subtitle)}</p>}
        </Reveal>
        <Reveal className="rounded-xl overflow-hidden h-[420px]" style={{ borderWidth: 1, borderStyle: 'solid', borderColor: m.borderColor }}>
          <MapContainer center={center} zoom={5} style={{ height: '100%', width: '100%' }}>
            <TileLayer url={getTileUrl(lang)} attribution={getTileAttribution(lang)} />
            <MarkerClusterGroup>
              {all.map((loc, i) => (<Marker key={i} position={[loc.lat, loc.lng]}><Popup><strong>{loc.title || loc.name}</strong>{loc.description && <p>{loc.description}</p>}{loc.link && <a href={loc.link} target={loc.open_in_new_tab ? '_blank' : '_self'} rel="noreferrer" className="text-blue-500 underline">Visit</a>}</Popup></Marker>))}
            </MarkerClusterGroup>
          </MapContainer>
        </Reveal>
      </div>
    </section>
  );
}

// Portfolio
export function AurexPortfolioMono({ items, bg, font, cmsConfig = {} }) {
  const tt = useT();
  const { lang } = useLang();
  const filtered = (items || []).filter(i => itemHasLocale(i.title, lang));
  if (!filtered.length) return null;
  const m = monoStyle(bg, font, '#FFFFFF');
  return (
    <section className={monoShell} style={m.style} id="portfolio" data-testid="portfolio-section">
      <div className="max-w-7xl mx-auto">
        <Reveal className="flex items-end justify-between mb-12 flex-wrap gap-4">
          <div>
            <p className={`text-[11px] uppercase tracking-[0.3em] font-semibold ${m.eyebrowClass} mb-3`}>{tt(cmsConfig.eyebrow) || 'Our work'}</p>
            <h2 className="text-3xl md:text-5xl font-bold tracking-tight" data-testid="portfolio-title">{tt(cmsConfig.title) || 'Featured Projects'}</h2>
            {tt(cmsConfig.subtitle) && <p className={`mt-3 ${m.mutedClass}`}>{tt(cmsConfig.subtitle)}</p>}
          </div>
          <Link to={tt(cmsConfig.cta_url) || '/featured-projects'} target={cmsConfig.cta_new_tab ? '_blank' : '_self'} rel="noopener noreferrer" className="text-sm font-medium inline-flex items-center gap-1 hover:gap-2 transition-all" data-testid="portfolio-view-all">{tt(cmsConfig.cta_text) || 'View all'} <ArrowRight className="w-4 h-4" /></Link>
        </Reveal>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {filtered.slice(0, 4).map((p, idx) => (
            <Reveal delay={idx * 100} key={p.id} className="group relative rounded-xl overflow-hidden" style={{ borderWidth: 1, borderStyle: 'solid', borderColor: m.borderColor }}>
              {p.image && <img src={resolveImg(p.image)} alt="" className="w-full h-80 object-cover transition-all duration-500 group-hover:scale-105" style={{ filter: 'grayscale(30%)' }} />}
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent flex items-end p-7 opacity-90 group-hover:opacity-100 transition-opacity">
                <div className="flex-1">
                  <p className="text-[10px] uppercase tracking-wider text-white/70 mb-1">{p.category}</p>
                  <h3 className="text-xl font-semibold text-white">{p.title}</h3>
                </div>
                {p.link && <a href={p.link} target={p.open_in_new_tab ? '_blank' : '_self'} rel="noreferrer" className="w-10 h-10 rounded-full bg-white/15 flex items-center justify-center text-white hover:bg-white/30 flex-shrink-0 backdrop-blur-sm"><ArrowUpRight className="w-4 h-4" /></a>}
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

// Gallery
export function AurexGalleryMono({ items, bg, font, cmsConfig = {} }) {
  const tt = useT();
  const { lang } = useLang();
  const filtered = (items || []).filter(i => itemHasLocale(i.title, lang));
  if (!filtered.length) return null;
  const m = monoStyle(bg, font, '#F9FAFB');
  return (
    <section className={monoShell} style={m.style} id="gallery" data-testid="gallery-section">
      <div className="max-w-7xl mx-auto">
        <Reveal className="flex items-end justify-between mb-12 flex-wrap gap-4">
          <div>
            <p className={`text-[11px] uppercase tracking-[0.3em] font-semibold ${m.eyebrowClass} mb-3`}>{tt(cmsConfig.eyebrow) || 'Moments'}</p>
            <h2 className="text-3xl md:text-5xl font-bold tracking-tight" data-testid="gallery-title">{tt(cmsConfig.title) || 'Gallery'}</h2>
            {tt(cmsConfig.subtitle) && <p className={`mt-3 ${m.mutedClass}`}>{tt(cmsConfig.subtitle)}</p>}
          </div>
          <Link to={tt(cmsConfig.cta_url) || '/gallery'} target={cmsConfig.cta_new_tab ? '_blank' : '_self'} rel="noopener noreferrer" className="text-sm font-medium inline-flex items-center gap-1 hover:gap-2 transition-all">{tt(cmsConfig.cta_text) || 'View all'} <ArrowRight className="w-4 h-4" /></Link>
        </Reveal>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {filtered.slice(0, 6).map((img, idx) => (
            <Reveal delay={idx * 60} key={img.id} className="group overflow-hidden rounded-xl" style={{ borderWidth: 1, borderStyle: 'solid', borderColor: m.borderColor }}>
              <img src={resolveImg(img.image)} alt={img.title} className="w-full aspect-square object-cover group-hover:scale-105 transition-all duration-500" style={{ filter: 'grayscale(40%)' }} onMouseEnter={e => (e.currentTarget.style.filter = 'grayscale(0%)')} onMouseLeave={e => (e.currentTarget.style.filter = 'grayscale(40%)')} />
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

// Testimonials — carousel: arrows + dots, 3 visible on desktop, 1 on mobile.
// ─── Private Community Pro — Testimonials (Litho "What Our Client Say?") ────
// Centered single-quote carousel: eyebrow pill, mixed-weight heading, faded
// green quote marks, star rating, italic quote, avatar (photo → initials),
// name + role, dot pagination. Switched in HomePage when theme === 'privatecommunity'.
export function PcpTestimonials({ items, bg, font, favicon, cmsConfig = {} }) {
  const tt = useT();
  const { lang } = useLang();
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const [cycle, setCycle] = useState(0); // bump to restart the auto-advance timer after manual nav
  const m = monoStyle(bg, font, '#FFFFFF');
  const filtered = (items || []).filter(t => t.visible !== false && itemHasLocale(t.content, lang));
  useEffect(() => { setIdx(0); }, [lang, filtered.length]);
  // Auto-advance so every testimonial is read without clicking. Pauses on hover,
  // respects reduced-motion; a manual dot click restarts the countdown (cycle).
  useEffect(() => {
    if (filtered.length <= 1 || paused) return;
    if (typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const id = setInterval(() => setIdx(i => (i + 1) % filtered.length), 6000);
    return () => clearInterval(id);
  }, [filtered.length, paused, cycle]);
  if (!filtered.length) return null;
  const cur = Math.min(idx, filtered.length - 1);
  const t = filtered[cur];

  const heading  = m.isDark ? '#FFFFFF' : '#1a2332';
  const muted    = m.isDark ? 'rgba(255,255,255,0.6)' : '#5b6570';
  const quoteCol = m.isDark ? 'rgba(255,255,255,0.9)' : '#2b3442';
  const pillBg   = m.isDark ? 'rgba(255,255,255,0.06)' : '#ffffff';
  const pillBd   = m.isDark ? 'rgba(255,255,255,0.14)' : '#ececec';
  const dotIdle  = m.isDark ? 'rgba(255,255,255,0.3)' : 'var(--accent-tint-2)';
  const accent   = 'var(--color-accent, #73af5f)';
  const faviconUrl = favicon ? resolveImg(favicon) : null;

  // Mixed-weight headline: first + last upright, middle Playfair-italic green.
  const renderHeadline = (title) => {
    const words = String(title || '').trim().split(/\s+/).filter(Boolean);
    if (words.length <= 2) return <>{title}</>;
    const first = words[0], last = words[words.length - 1], mid = words.slice(1, -1).join(' ');
    return <>{first}{' '}<span style={{ fontFamily: "'Playfair Display', Georgia, serif", fontStyle: 'italic', fontWeight: 600, color: 'var(--accent-deep)' }}>{mid}</span>{' '}{last}</>;
  };

  const photo = t.image || t.avatar;
  const roleLabel = tt(t.title) || tt(t.role);
  const company = tt(t.company);
  const rating = Math.max(0, Math.min(5, Math.round(Number(t.rating) || 5)));
  const eyebrow = tt(cmsConfig.eyebrow);
  const title = tt(cmsConfig.title);
  const subtitle = tt(cmsConfig.subtitle);
  const QUOTE_PATH = "M0 70V38C0 17 14 3 36 0l4 12C26 15 18 22 17 32h17v38H0Zm48 0V38C48 17 62 3 84 0l4 12C74 15 66 22 65 32h17v38H48Z";

  return (
    <section className={monoShell} style={m.style} id="testimonials" data-testid="testimonials-section">
      <div className="max-w-[820px] mx-auto text-center">
        {eyebrow && (
          <div className="inline-flex items-center gap-2.5 mb-6" style={{ padding: '7px 16px 7px 8px', borderRadius: 999, background: pillBg, border: `1px solid ${pillBd}`, boxShadow: m.isDark ? 'none' : '0 2px 10px rgba(17,24,39,0.05)' }}>
            <span className="inline-flex items-center justify-center overflow-hidden" style={{ width: 30, height: 30, borderRadius: 999, background: m.isDark ? 'rgba(255,255,255,0.08)' : '#f5f7f4' }}>
              {faviconUrl ? <img src={faviconUrl} alt="" className="w-full h-full object-contain" /> : <span style={{ width: 7, height: 7, borderRadius: 999, background: accent }} />}
            </span>
            {eyebrow && <span className="text-[11px] font-semibold uppercase" style={{ letterSpacing: '.18em', color: m.isDark ? 'rgba(255,255,255,0.8)' : 'var(--accent-deep)' }}>{eyebrow}</span>}
          </div>
        )}
        {title && <h2 className="font-extrabold" style={{ fontSize: 'clamp(2rem, 3.4vw, 2.9rem)', lineHeight: 1.08, letterSpacing: '-.02em', color: heading }} data-testid="testimonials-title">{renderHeadline(title)}</h2>}
        {subtitle && <p className="mx-auto mt-4" style={{ maxWidth: 520, fontSize: 16, lineHeight: 1.6, color: muted }}>{subtitle}</p>}

        <div className="relative" style={{ marginTop: 52 }} onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}>
          <svg className="absolute pointer-events-none" style={{ left: -6, top: -30, opacity: .12 }} width="88" height="70" viewBox="0 0 88 70" fill={accent}><path d={QUOTE_PATH} /></svg>
          <svg className="absolute pointer-events-none" style={{ right: -6, bottom: -34, opacity: .12, transform: 'rotate(180deg)' }} width="88" height="70" viewBox="0 0 88 70" fill={accent}><path d={QUOTE_PATH} /></svg>

          <div key={cur} style={{ animation: 'pcp-fade .5s ease' }}>
          {rating > 0 && (
            <div className="flex items-center justify-center gap-1.5" data-testid="testimonial-rating">
              {Array.from({ length: rating }).map((_, i) => (
                <svg key={i} width="22" height="22" viewBox="0 0 24 24" fill={accent}><path d="m12 2 2.9 6.3 6.9.7-5.1 4.6 1.4 6.8L12 17.7 5.9 20.4l1.4-6.8L2.2 9l6.9-.7L12 2Z" /></svg>
              ))}
            </div>
          )}

          <p className="mx-auto" style={{ fontFamily: "'Playfair Display', Georgia, serif", fontStyle: 'italic', fontWeight: 500, marginTop: 22, maxWidth: 660, fontSize: 'clamp(1.25rem, 2vw, 1.7rem)', lineHeight: 1.5, color: quoteCol }} data-testid="testimonial-quote">{'“'}{tt(t.content)}{'”'}</p>

          <div className="flex justify-center" style={{ marginTop: 34 }}>
            {photo ? (
              <img src={resolveImg(photo)} alt={tt(t.name)} style={{ width: 74, height: 74, borderRadius: 999, objectFit: 'cover', border: `2px solid ${m.isDark ? 'rgba(255,255,255,0.2)' : 'var(--accent-tint-2)'}`, boxShadow: '0 10px 26px -14px color-mix(in srgb, var(--accent-deep) 50%, transparent)' }} />
            ) : (
              <div style={{ width: 74, height: 74, borderRadius: 999, background: m.isDark ? 'rgba(255,255,255,0.08)' : 'var(--accent-tint)', border: `2px solid ${m.isDark ? 'rgba(255,255,255,0.2)' : 'var(--accent-tint-2)'}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <span style={{ fontWeight: 700, fontSize: 22, color: 'var(--accent-deep)' }}>{(tt(t.name) || '?').charAt(0).toUpperCase()}</span>
              </div>
            )}
          </div>
          <div style={{ marginTop: 14, fontWeight: 700, fontSize: 19, color: heading }} data-testid="testimonial-name">{tt(t.name)}</div>
          {(roleLabel || company) && <div style={{ marginTop: 3, fontSize: 12, letterSpacing: '.18em', textTransform: 'uppercase', color: muted }}>{roleLabel}{roleLabel && company ? ' · ' : ''}{company}</div>}
          </div>
        </div>

        {filtered.length > 1 && (
          <div className="flex items-center justify-center gap-2" style={{ marginTop: 40 }}>
            {filtered.map((_, i) => (
              <button key={i} onClick={() => { setIdx(i); setCycle(c => c + 1); }} aria-label={`Testimonial ${i + 1}`} className="rounded-full transition-all" style={{ width: i === cur ? 26 : 8, height: 8, background: i === cur ? accent : dotIdle }} data-testid={`testimonials-dot-${i}`} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

export function AurexTestimonialsMono({ items, bg, font, cmsConfig = {} }) {
  const tt = useT();
  const { lang } = useLang();
  const m = monoStyle(bg, font, '#FFFFFF');
  const [page, setPage] = useState(0);
  const [perView, setPerView] = useState(3);
  useEffect(() => {
    const calc = () => setPerView(window.innerWidth < 640 ? 1 : window.innerWidth < 1024 ? 2 : 3);
    calc();
    window.addEventListener('resize', calc);
    return () => window.removeEventListener('resize', calc);
  }, []);
  // Filter testimonials by locale AND visibility. Items marked `visible:
  // false` in the CMS are hidden on the public site — this matches how
  // admins expect to disable a quote without deleting it.
  const filtered = (items || []).filter(t => t.visible !== false && itemHasLocale(t.content, lang));
  // Reset page when filter changes to avoid out-of-range index
  useEffect(() => { setPage(0); }, [lang, filtered.length]);
  if (!filtered.length) return null;
  const pageCount = Math.max(1, Math.ceil(filtered.length / perView));
  const go = (n) => setPage((n + pageCount) % pageCount);
  const visible = filtered.slice(page * perView, page * perView + perView);
  return (
    <section className={monoShell} style={m.style} id="testimonials" data-testid="testimonials-section">
      <div className="max-w-7xl mx-auto">
        <Reveal className="text-center mb-14">
          {tt(cmsConfig.eyebrow) && <p className={`text-[11px] uppercase tracking-[0.3em] font-semibold ${m.eyebrowClass} mb-3`}>{tt(cmsConfig.eyebrow)}</p>}
          {tt(cmsConfig.title) && <h2 className="text-3xl md:text-5xl font-bold tracking-tight mb-3" data-testid="testimonials-title" style={{ fontFamily: "'Playfair Display', serif" }}>{tt(cmsConfig.title)}</h2>}
          {tt(cmsConfig.subtitle) && <p className={`text-sm ${m.mutedClass}`}>{tt(cmsConfig.subtitle)}</p>}
        </Reveal>
        <div className="relative">
          <button onClick={() => go(page - 1)} aria-label="Previous testimonials" className={`absolute -left-2 md:-left-8 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full flex items-center justify-center transition-colors hover:opacity-80 ${m.isDark ? 'bg-white/10 text-white hover:bg-white/20' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} data-testid="testimonials-prev">
            <lucide.ChevronLeft className="w-5 h-5" />
          </button>
          <div className="grid gap-6 px-2" style={{ gridTemplateColumns: `repeat(${perView}, minmax(0, 1fr))` }}>
            {visible.map((t, idx) => {
              // Backend stores: name, title (role/position), content, image (photo)
              const photo = t.image || t.avatar;
              const roleLabel = tt(t.title) || tt(t.role);
              return (
              <Reveal delay={idx * 100} key={`${page}-${t.id}`} className="rounded-xl p-8 text-center flex flex-col items-center" style={{ backgroundColor: m.isDark ? 'rgba(255,255,255,0.04)' : '#F3F4F6' }} data-testid={`testimonial-card-${t.id}`}>
                {/* Order per CMS feedback: quote → photo → name → role */}
                <p className={`text-sm md:text-base italic leading-relaxed mb-6 ${m.mutedClass}`}>"{tt(t.content)}"</p>
                {photo ? (
                  <img src={resolveImg(photo)} alt={tt(t.name)} className="w-20 h-20 rounded-full object-cover mb-5 border-2" style={{ borderColor: m.isDark ? 'rgba(255,255,255,0.15)' : '#E5E7EB' }} />
                ) : (
                  <div className="w-20 h-20 rounded-full bg-gray-200 mb-5 flex items-center justify-center text-xl font-bold text-gray-400">{tt(t.name)?.[0] || '?'}</div>
                )}
                <h3 className="font-bold text-lg md:text-xl" style={{ fontFamily: "'Playfair Display', serif" }}>{tt(t.name)}</h3>
                {roleLabel && <p className={`text-[11px] uppercase tracking-[0.2em] mt-2 ${m.eyebrowClass}`}>{roleLabel}{tt(t.company) ? ` · ${tt(t.company)}` : ''}</p>}
              </Reveal>
              );
            })}
          </div>
          <button onClick={() => go(page + 1)} aria-label="Next testimonials" className={`absolute -right-2 md:-right-8 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full flex items-center justify-center transition-colors hover:opacity-80 ${m.isDark ? 'bg-white/10 text-white hover:bg-white/20' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} data-testid="testimonials-next">
            <lucide.ChevronRight className="w-5 h-5" />
          </button>
        </div>
        {pageCount > 1 && (
          <div className="flex justify-center gap-2 mt-10">
            {Array.from({ length: pageCount }).map((_, i) => (
              <button key={i} onClick={() => setPage(i)} className={`rounded-full transition-all ${i === page ? 'w-6' : 'w-2 hover:opacity-60'} h-2`} style={{ backgroundColor: i === page ? (m.isDark ? '#FFFFFF' : '#111827') : (m.isDark ? 'rgba(255,255,255,0.3)' : 'rgba(17,24,39,0.3)') }} aria-label={`Page ${i + 1}`} data-testid={`testimonials-dot-${i}`} />
            ))}
          </div>
        )}
        {cmsConfig.cta_text && (
          <Reveal delay={150} className="text-center mt-10">
            <MonoButton href={cmsConfig.cta_url} text={cmsConfig.cta_text} newTab={cmsConfig.cta_new_tab} m={m} dataTestId="testimonials-cta" />
          </Reveal>
        )}
      </div>
    </section>
  );
}

// Contact
// ─── Private Community Pro — "Contact" (Option C: offset green panel) ───────
// Left pitch (eyebrow pill w/ favicon, mixed-weight headline, rich-text
// description) + a dark form card sitting on an offset green panel (echoes
// PcpAbout). Keeps the real functional form (contactAPI + captcha + toast).
// Switched in HomePage for privatecommunity. Falls back to AurexContactMono.
export function PcpContact({ contactSettings, bg, font, favicon }) {
  const tt = useT();
  const cs = contactSettings || {};
  const m = monoStyle(bg, font, '#f7f8f9');
  const [form, setForm] = useState({ name: '', email: '', message: '' });
  const [captchaToken, setCaptchaToken] = useState('');
  const [sending, setSending] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try { await contactAPI.submit({ ...form, captcha_token: captchaToken }); toast.success('Message sent!'); setForm({ name: '', email: '', message: '' }); setCaptchaToken(''); }
    catch (err) { toast.error(err?.response?.data?.detail || 'Failed to send'); }
    finally { setSending(false); }
  };

  const accent  = 'var(--color-accent)';
  const heading = m.isDark ? '#FFFFFF' : '#1a2332';
  const muted   = m.isDark ? 'rgba(255,255,255,0.66)' : '#5b6570';
  const descCol = m.isDark ? 'rgba(255,255,255,0.7)' : '#4a5560';
  const pillBg  = m.isDark ? 'rgba(255,255,255,0.05)' : '#ffffff';
  const pillBd  = m.isDark ? 'rgba(255,255,255,0.12)' : 'color-mix(in srgb, var(--color-accent) 28%, transparent)';
  const faviconUrl = favicon ? resolveImg(favicon) : null;

  // The "Request access" card is a self-contained panel, so it must contrast
  // with the SECTION, not match it: a dark section gets a LIGHT card (and vice
  // versa). Everything inside the card derives from `panelDark`.
  const panelDark  = !m.isDark;
  const panelBg    = panelDark ? '#111827' : '#ffffff';
  const panelBd    = panelDark ? 'rgba(255,255,255,0.06)' : '#e5e7eb';
  const panelTitle = panelDark ? '#ffffff' : '#1a2332';
  const panelLabel = panelDark ? 'rgba(255,255,255,0.5)' : '#6b7280';

  const renderHeadline = (t) => {
    const words = String(t || '').trim().split(/\s+/).filter(Boolean);
    if (words.length <= 2) return <>{t}</>;
    const first = words[0], last = words[words.length - 1], mid = words.slice(1, -1).join(' ');
    return (<>{first} <span style={{ fontFamily: "'Playfair Display', Georgia, serif", fontStyle: 'italic', fontWeight: 500, color: accent }}>{mid}</span> {last}</>);
  };

  const inputClass = panelDark
    ? 'w-full px-4 py-3.5 rounded-xl text-sm bg-white/5 border border-white/15 text-white placeholder:text-white/40 focus:outline-none focus:border-[color:var(--color-accent)] transition-colors'
    : 'w-full px-4 py-3.5 rounded-xl text-sm bg-gray-50 border border-gray-200 text-gray-900 placeholder:text-gray-400 focus:outline-none focus:border-[color:var(--color-accent)] transition-colors';
  const labelStyle = { display: 'block', fontSize: 11, letterSpacing: '0.1em', textTransform: 'uppercase', fontWeight: 600, color: panelLabel, marginBottom: 7 };

  return (
    <section className={monoShell} style={{ ...m.style, position: 'relative', overflow: 'hidden' }} id="contact" data-testid="contact-section">
      {!m.isDark && <div aria-hidden="true" style={{ position: 'absolute', top: -160, right: -150, width: 460, height: 460, borderRadius: 999, background: 'radial-gradient(circle, color-mix(in srgb, var(--color-accent) 9%, transparent), color-mix(in srgb, var(--color-accent) 0%, transparent) 70%)', pointerEvents: 'none' }} />}
      <div className="max-w-6xl mx-auto relative grid grid-cols-1 lg:grid-cols-[1fr,1.05fr] gap-12 lg:gap-16 items-center">

        {/* pitch */}
        <Reveal>
          {(faviconUrl || tt(cs.title)) && (
            <div className="inline-flex items-center gap-2.5 mb-6" style={{ padding: '7px 16px 7px 7px', borderRadius: 999, background: pillBg, border: `1px solid ${pillBd}`, boxShadow: m.isDark ? 'none' : '0 1px 3px rgba(0,0,0,0.06)' }}>
              <span className="inline-flex items-center justify-center overflow-hidden" style={{ width: 30, height: 30, borderRadius: 999, background: m.isDark ? 'rgba(255,255,255,0.08)' : '#f5f7f4' }}>
                {faviconUrl ? <img src={faviconUrl} alt="" style={{ width: 22, height: 'auto', display: 'block' }} /> : <span style={{ width: 12, height: 12, borderRadius: 999, background: accent }} />}
              </span>
              {tt(cs.title) && <span style={{ fontSize: 12, letterSpacing: '0.18em', textTransform: 'uppercase', fontWeight: 600, color: m.isDark ? 'rgba(255,255,255,0.78)' : 'var(--accent-strong)' }}>{tt(cs.title)}</span>}
            </div>
          )}
          {tt(cs.subtitle) && (
            <h2 data-testid="contact-title" style={{ margin: '0 0 18px', fontFamily: "'Sora', sans-serif", fontWeight: 800, fontSize: 'clamp(32px, 3.6vw, 46px)', lineHeight: 1.05, letterSpacing: '-0.02em', color: heading }}>
              {renderHeadline(tt(cs.subtitle))}
            </h2>
          )}
          {tt(cs.description) && <div className="rich-text-content" style={{ fontSize: 15.5, lineHeight: 1.7, color: descCol, maxWidth: 460 }} dangerouslySetInnerHTML={{ __html: normalizeRichText(tt(cs.description)) }} />}
        </Reveal>

        {/* form on offset green panel */}
        <Reveal delay={120} className="relative">
          <div aria-hidden="true" style={{ position: 'absolute', inset: '22px -22px -22px 22px', borderRadius: 24, background: accent }} />
          <form onSubmit={submit} className="relative" style={{ borderRadius: 22, padding: '34px 36px 32px', background: panelBg, border: `1px solid ${panelBd}`, boxShadow: panelDark ? '0 30px 70px -40px rgba(0,0,0,.5)' : '0 30px 70px -40px rgba(17,24,39,.22)' }}>
            <h3 style={{ margin: '0 0 22px', fontFamily: "'Sora', sans-serif", fontSize: 20, fontWeight: 700, color: panelTitle }}>Request access</h3>
            <div style={{ marginBottom: 15 }}>
              <label style={labelStyle}>Your name</label>
              <input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} required placeholder={tt(cs.name_placeholder) || 'Your name'} className={inputClass} />
            </div>
            <div style={{ marginBottom: 15 }}>
              <label style={labelStyle}>Your email</label>
              <input type="email" value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} required placeholder={tt(cs.email_placeholder) || 'Your email'} className={inputClass} />
            </div>
            <div style={{ marginBottom: 18 }}>
              <label style={labelStyle}>Message</label>
              <textarea value={form.message} onChange={e => setForm(p => ({ ...p, message: e.target.value }))} required placeholder={tt(cs.message_placeholder) || 'Your message'} rows={3} className={`${inputClass} resize-none`} />
            </div>
            <div className="mb-4"><CaptchaWidget onChange={setCaptchaToken} testId="contact-captcha" theme={panelDark ? 'dark' : 'light'} /></div>
            <button type="submit" disabled={sending} className="pcpb pcpb--green w-full justify-center disabled:opacity-50" data-contrast={panelDark ? 'dark' : 'light'} style={{ height: 52, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase' }} data-testid="contact-submit">
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} {tt(cs.submit_text) || 'Send message'}
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
}

export function AurexContactMono({ contactSettings, bg, font }) {
  const tt = useT();
  const cs = contactSettings || {};
  const m = monoStyle(bg, font, '#111827');
  const [form, setForm] = useState({ name: '', email: '', message: '' });
  const [captchaToken, setCaptchaToken] = useState('');
  const [sending, setSending] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try { await contactAPI.submit({ ...form, captcha_token: captchaToken }); toast.success('Message sent!'); setForm({ name: '', email: '', message: '' }); setCaptchaToken(''); }
    catch (err) { toast.error(err?.response?.data?.detail || 'Failed to send'); }
    finally { setSending(false); }
  };
  // When dark: transparent inputs; when light: soft gray inputs
  const inputClass = m.isDark
    ? 'w-full px-5 py-3.5 bg-white/5 border border-white/15 rounded-lg text-sm placeholder:text-white/40 focus:outline-none focus:border-white/60 transition-colors'
    : 'w-full px-5 py-3.5 bg-gray-50 border border-gray-200 rounded-lg text-sm placeholder:text-gray-400 focus:outline-none focus:border-gray-800 transition-colors';
  return (
    <section className={monoShell} style={m.style} id="contact" data-testid="contact-section">
      <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
        <Reveal>
          {tt(cs.title) && <p className={`text-[11px] uppercase tracking-[0.3em] font-semibold ${m.eyebrowClass} mb-4`}>{tt(cs.title)}</p>}
          {tt(cs.subtitle) && <h2 className="text-3xl md:text-5xl font-bold tracking-tight mb-5" data-testid="contact-title">{tt(cs.subtitle)}</h2>}
          <div className="w-12 h-px mb-6" style={{ backgroundColor: m.isDark ? 'rgba(255,255,255,0.4)' : '#111827' }} />
          {tt(cs.description) && <div className={`${m.mutedClass} leading-relaxed rich-text-content`} dangerouslySetInnerHTML={{ __html: normalizeRichText(tt(cs.description)) }} />}
        </Reveal>
        <Reveal delay={120} as="form" onSubmit={submit} className="space-y-4">
          <input value={form.name} onChange={e => setForm(p => ({...p, name: e.target.value}))} required placeholder={tt(cs.name_placeholder) || 'Your name'} className={inputClass} style={{ color: 'inherit' }} />
          <input type="email" value={form.email} onChange={e => setForm(p => ({...p, email: e.target.value}))} required placeholder={tt(cs.email_placeholder) || 'Your email'} className={inputClass} style={{ color: 'inherit' }} />
          <textarea value={form.message} onChange={e => setForm(p => ({...p, message: e.target.value}))} required placeholder={tt(cs.message_placeholder) || 'Your message'} rows={5} className={`${inputClass} resize-none`} style={{ color: 'inherit' }} />
          <CaptchaWidget onChange={setCaptchaToken} testId="contact-captcha" />
          <button type="submit" disabled={sending} className="w-full py-3 rounded-full font-semibold text-sm inline-flex items-center justify-center gap-2 disabled:opacity-50 transition-colors" style={{ backgroundColor: m.isDark ? '#FFFFFF' : '#111827', color: m.isDark ? '#111827' : '#FFFFFF' }} data-testid="contact-submit">{sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} {tt(cs.submit_text) || 'Send message'}</button>
        </Reveal>
      </div>
    </section>
  );
}

// ─── Hero (Aurex monochrome) ─────────────────────────────────────────────
// Supports CMS slides + countdown + single-column typography-forward layout.
// Falls back to the optional photo — rendered in grayscale — as a side column.

// A/B testing helpers — deterministic per-browser bucket, persisted in localStorage.
function getVisitorId() {
  try {
    let v = localStorage.getItem('aurex_visitor_id');
    if (!v) {
      v = `v_${Math.random().toString(36).slice(2)}_${Date.now().toString(36)}`;
      localStorage.setItem('aurex_visitor_id', v);
    }
    return v;
  } catch { return 'anon'; }
}
function getVariantFor(slideId) {
  if (!slideId) return 'A';
  try {
    const key = `aurex_ab_${slideId}`;
    let v = localStorage.getItem(key);
    if (!v) {
      v = Math.random() < 0.5 ? 'A' : 'B';
      localStorage.setItem(key, v);
    }
    return v;
  } catch { return 'A'; }
}
function logHeroEvent(payload) {
  try {
    fetch(`${API}/api/public/hero-cta-event`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      keepalive: true, // allow tracking on click-navigate
    }).catch(() => {});
  } catch { /* noop */ }
}

function countdownParts(target) {
  if (!target) return null;
  const t = new Date(target).getTime();
  const diff = t - Date.now();
  if (isNaN(t) || diff <= 0) return null;
  const days = Math.floor(diff / 86400000);
  const hours = Math.floor((diff % 86400000) / 3600000);
  const mins = Math.floor((diff % 3600000) / 60000);
  const secs = Math.floor((diff % 60000) / 1000);
  return { days, hours, mins, secs };
}

function CountdownMono({ target, light = false }) {
  const [parts, setParts] = useState(() => countdownParts(target));
  useEffect(() => {
    if (!target) return;
    const id = setInterval(() => setParts(countdownParts(target)), 1000);
    return () => clearInterval(id);
  }, [target]);
  if (!parts) return null;
  const units = [['Days', parts.days], ['Hours', parts.hours], ['Min', parts.mins], ['Sec', parts.secs]];
  return (
    <div className="flex gap-6 md:gap-10 my-8" data-testid="hero-countdown">
      {units.map(([lbl, n]) => (
        <div key={lbl}>
          <div className={`text-4xl md:text-6xl font-bold tabular-nums leading-none ${light ? 'text-gray-900' : ''}`}>{String(n).padStart(2, '0')}</div>
          <div className={`text-[10px] uppercase tracking-[0.25em] mt-2 ${light ? 'text-gray-600' : 'text-white/50'}`}>{lbl}</div>
        </div>
      ))}
    </div>
  );
}

const stripHtml = (h) => (h || '').replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();

export function AurexHeroMono({ slides, data }) {
  const tt = useT();
  const allSlides = (slides && slides.length > 0 ? slides : (data?.title ? [data] : []));
  const [idx, setIdx] = useState(0);
  const [scrollY, setScrollY] = useState(0);
  useEffect(() => {
    if (allSlides.length <= 1) return;
    const delay = allSlides[idx]?.delay || 9400;
    const t = setTimeout(() => setIdx(i => (i + 1) % allSlides.length), delay);
    return () => clearTimeout(t);
  }, [idx, allSlides]);
  useEffect(() => {
    const onScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  // Fire impressions for the visible slide's A/B-tracked buttons. Runs on every
  // slide change but only emits when ab_testing is on and the button has both
  // A and B variant text set.
  const curSlide = allSlides[idx];
  const curVariant = curSlide?.ab_testing_enabled ? getVariantFor(curSlide.id) : 'A';
  useEffect(() => {
    if (!curSlide?.ab_testing_enabled || !curSlide?.id) return;
    const vid = getVisitorId();
    [0, 1, 2].forEach(i => {
      const key = i === 0 ? '' : `_${i + 1}`;
      const textA = curSlide[`button${key}_text`];
      const textB = curSlide[`button${key}_text_variant_b`];
      if (textA && textB) {
        logHeroEvent({ slide_id: curSlide.id, button_index: i, variant: curVariant, event_type: 'impression', visitor_id: vid });
      }
    });
  }, [curSlide?.id, curVariant]); // eslint-disable-line react-hooks/exhaustive-deps
  if (allSlides.length === 0) return null;
  const s = allSlides[idx];
  const bg = s.background || s.background_image || '';

  // A/B testing: decide variant once per slide per visitor (persisted in localStorage).
  const abOn = !!s.ab_testing_enabled;
  const variant = abOn ? getVariantFor(s.id) : 'A';
  const pickText = (baseText, variantBText) => (abOn && variant === 'B' && variantBText ? variantBText : baseText);

  // Collect up to 3 CTAs into a single array. Filter by the translated text
  // so CTAs without a translation in the active locale are hidden.
  const ctas = [
    { text: pickText(s.button_text,   s.button_text_variant_b),   url: s.button_url   || s.button_link, target: s.window_open === 'new' ? '_blank' : '_self' },
    { text: pickText(s.button_2_text, s.button_2_text_variant_b), url: s.button_2_url, target: s.button_2_window_open === 'new' ? '_blank' : '_self' },
    { text: pickText(s.button_3_text, s.button_3_text_variant_b), url: s.button_3_url, target: s.button_3_window_open === 'new' ? '_blank' : '_self' },
  ].filter(c => tt(c.text));

  const onCtaClick = (buttonIndex) => {
    if (!abOn || !s?.id) return;
    const key = buttonIndex === 0 ? '' : `_${buttonIndex + 1}`;
    // Only track buttons that have a B variant (ie. actually in the test)
    if (!s[`button${key}_text_variant_b`]) return;
    logHeroEvent({ slide_id: s.id, button_index: buttonIndex, variant, event_type: 'click', visitor_id: getVisitorId() });
  };

  // Parallax: image translates 0 → +80px as user scrolls 0 → 600px
  const parallax = Math.min(scrollY * 0.25, 120);

  return (
    <section className="aurex-section relative overflow-hidden bg-white" data-testid="hero-section" style={{ fontFamily: "'Inter', sans-serif" }}>
      {bg && (
        <>
          <div className="absolute inset-0 bg-cover bg-center will-change-transform" style={{ backgroundImage: `url(${bg})`, transform: `translate3d(0, ${parallax}px, 0) scale(1.08)` }} />
          {/* Soft light overlay that fades from bright-left to transparent-right to keep text legible without killing the photo */}
          <div className="absolute inset-0" style={{ background: 'linear-gradient(90deg, rgba(255,255,255,0.85) 0%, rgba(255,255,255,0.55) 45%, rgba(255,255,255,0.05) 85%)' }} />
        </>
      )}
      <div className="relative max-w-7xl mx-auto px-6 sm:px-10 md:px-16 lg:px-24 py-24 md:py-36 min-h-[620px] md:min-h-[760px] flex items-end">
        <div className="w-full max-w-3xl">
          {s.subtitle && (
            <Reveal>
              <p className="text-[11px] uppercase tracking-[0.3em] font-semibold text-gray-700 mb-5" data-testid="hero-subtitle" dangerouslySetInnerHTML={{ __html: stripHtml(tt(s.subtitle)) }} />
            </Reveal>
          )}
          {s.title && (
            <Reveal delay={100}>
              <h1 className="text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-bold leading-[0.95] tracking-tight text-gray-900 mb-8" data-testid="hero-title">
                {(() => { const title = tt(s.title); return typeof title === 'string' && title.includes('<') ? <span dangerouslySetInnerHTML={{ __html: normalizeRichText(title) }} /> : title?.split('\n').map((line, i) => <React.Fragment key={i}>{i > 0 && <br />}<span className={i > 0 ? 'italic font-light text-gray-700' : ''}>{line}</span></React.Fragment>); })()}
              </h1>
            </Reveal>
          )}
          {s.countdown_to && <Reveal delay={200}><CountdownMono target={s.countdown_to} light /></Reveal>}
          {s.description && (
            <Reveal delay={250}>
              <div className="text-base md:text-lg text-gray-700 max-w-xl leading-relaxed rich-text-content mb-10" data-testid="hero-description" dangerouslySetInnerHTML={{ __html: normalizeRichText(tt(s.description)) }} />
            </Reveal>
          )}
          {ctas.length > 0 && (
            <Reveal delay={300}>
              <div className="flex flex-wrap items-center gap-3" data-testid="hero-cta-row">
                {ctas.map((c, i) => {
                  const primary = i === 0;
                  return (
                    <a
                      key={i}
                      href={tt(c.url) || '#'}
                      target={c.target}
                      rel="noopener noreferrer"
                      onClick={() => onCtaClick(i)}
                      className={`inline-flex items-center gap-2 px-7 py-3 rounded-full text-sm font-semibold transition-all hover:gap-3 ${primary ? 'bg-white text-gray-900 border border-gray-900 hover:bg-gray-900 hover:text-white' : 'bg-transparent text-gray-900 border-2 border-gray-900/80 hover:bg-gray-900 hover:text-white'}`}
                      data-testid={`hero-cta-btn-${i}`}
                    >
                      {tt(c.text)} {primary && <ArrowRight className="w-4 h-4" />}
                    </a>
                  );
                })}
              </div>
            </Reveal>
          )}
        </div>
      </div>
      {allSlides.length > 1 && (
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex gap-2 z-10">
          {allSlides.map((_, i) => (
            <button key={i} onClick={() => setIdx(i)} className={`h-[2px] transition-all ${i === idx ? 'w-10 bg-gray-900' : 'w-6 bg-gray-900/30 hover:bg-gray-900/60'}`} aria-label={`Slide ${i + 1}`} data-testid={`hero-dot-${i}`} />
          ))}
        </div>
      )}
    </section>
  );
}

// CSS keyframe + reveal styles (injected once)
if (typeof document !== 'undefined' && !document.getElementById('aurex-keyframes')) {
  const style = document.createElement('style');
  style.id = 'aurex-keyframes';
  style.textContent = `
    @keyframes aurex-scroll { from { transform: translateX(0); } to { transform: translateX(-50%); } }
    .aurex-reveal { opacity: 0; transform: translateY(24px); transition: opacity .7s ease-out, transform .7s ease-out; will-change: opacity, transform; }
    .aurex-reveal.aurex-in { opacity: 1; transform: translateY(0); }
    @media (prefers-reduced-motion: reduce) {
      .aurex-reveal { opacity: 1 !important; transform: none !important; transition: none !important; }
    }
    /* Let each Aurex section control its own typography (override global h1-h4 Playfair rule) */
    .aurex-section h1, .aurex-section h2, .aurex-section h3, .aurex-section h4 { font-family: inherit; }
  `;
  document.head.appendChild(style);
}
